import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  Legend,
  LabelList
} from 'recharts';
import {
  BarChart3,
  Check,
  ChevronDown,
  Download,
  FileSpreadsheet,
  Loader2,
  RefreshCw,
  TrendingDown,
  TrendingUp,
  Calendar,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import JSZip from 'jszip';

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useJobs } from '../../hooks/useJobs';
import { reportsApi } from '../../lib/api/reportsApi';
import { parseBkRejectionNote } from '../../lib/bkRejectReasons';
import { cn } from '../../lib/utils';
import {
  buildRegionColumns,
  buildRegionOptions,
  buildRoleColumns,
  candidateRoleId,
  makeRegionResolver,
  scopeJobsToRegions,
  scopeScreeningsToRegions,
} from './bk/bkColumns';

/* ──────────────────────────────────────────────────────────────────
   CONSTANTS
   ────────────────────────────────────────────────────────────── */

const OUTCOME_COLORS = {
  Selected: '#16a34a',
  Dropped: '#f97316',
  Rejected: '#dc2626',
  'On hold': '#d97706',
  Undecided: '#94a3b8',
};

const JOB_COLORS = [
  '#007BFF', '#16a34a', '#d97706', '#dc2626', '#8b5cf6', '#0891b2', '#e11d48', '#4f46e5',
];

const CATEGORY_COLORS = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#6366f1'
];
const EXPERIENCE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#6b7280'];

const CHART_MAX_CATEGORIES = 7;

/** Column axes offered by the COMPARE toggle, and the noun each one puts in table headers. */
const COMPARISON_LABELS = {
  role: 'Hiring Position',
  region: 'Region',
  month: 'Month',
};

/**
 * Single source of truth for the MR category breakdowns — drives the on-screen panels,
 * the visual modal titles and the Excel sheets, so the export always matches the screen.
 * `extract` returns the bucket a candidate falls into for that panel.
 */
const BASE_CATEGORY_PANELS = [
  { key: 'experience', chartTitle: 'Candidates by Experience', sheet: 'Candidates By Experience', header: 'Experience Band',
    extract: (c) => getExperienceBand(c.experienceYears) },
  { key: 'company', chartTitle: 'Top Current Companies', sheet: 'Top Current Companies', header: 'Current Company', topN: 25,
    extract: (c, ctx) => ctx.labelCompany(c) },
  // Hidden on screen by request, but still exported — flip `hiddenOnScreen` to bring the panel back.
  { key: 'screening', chartTitle: 'Candidates by Screening', sheet: 'Candidates By Screening', header: 'Screening',
    hiddenOnScreen: true,
    extract: (c) => c.screeningName || 'Unknown' },
];

/**
 * "Unknown" / "Not specified" is often the largest bucket, which would otherwise sort it to the
 * top of every panel. Pin those rows to the bottom instead, and keep them out of the top-N cut so
 * the row can never be swallowed into "Others".
 */
const UNKNOWN_BUCKET_LABELS = ['unknown', 'not specified', 'unassigned'];

function isUnknownBucket(name) {
  return UNKNOWN_BUCKET_LABELS.includes(String(name || '').trim().toLowerCase());
}

/** Count desc, with the unknown buckets forced last. */
function compareCategoryRows(a, b) {
  const aUnknown = isUnknownBucket(a.name);
  const bUnknown = isUnknownBucket(b.name);
  if (aUnknown !== bUnknown) return aUnknown ? 1 : -1;
  return b.count - a.count;
}

/** Excel rejects sheet names over 31 chars, containing : \ / ? * [ ], or duplicated. */
function excelSheetName(desired, used) {
  const base =
    String(desired || 'Sheet')
      .replace(/[:\\/?*[\]]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 31) || 'Sheet';
  let name = base;
  let n = 2;
  while (used.has(name.toLowerCase())) {
    const suffix = ` (${n})`;
    name = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    n += 1;
  }
  used.add(name.toLowerCase());
  return name;
}

function loosenLabel(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

/**
 * A date answer buckets one-per-candidate, so it never makes a useful breakdown. The apply
 * form types DOB inconsistently ('date', 'dob', or plain 'text' with a mask), so match the
 * label too rather than relying on fieldType alone.
 */
const CUSTOM_PANEL_EXCLUDED_TYPES = ['date', 'dob'];

/** Questions we deliberately do not chart. Date of Birth: excluded by request. */
const CUSTOM_PANEL_EXCLUDED_FIELDS = ['date of birth', 'dob'].map(loosenLabel);

/** Questions we do not list in the roster at all. Date of Birth: excluded by request. */
const CUSTOM_ROSTER_EXCLUDED_FIELDS = ['date of birth', 'dob'].map(loosenLabel);

function customFieldMatches(def, list) {
  return list.includes(loosenLabel(def.fieldId)) || list.includes(loosenLabel(def.label));
}

/**
 * Application questions are phrased as form prompts ("Division Applied for"), which reads
 * awkwardly in a report heading, so a trailing "for" is dropped. Display only — `fieldId` still
 * keys the answers and the application form itself is untouched. Applied after the exclusion
 * filters so DOB matching still sees the original label.
 */
function reportFieldLabel(label) {
  const raw = String(label || '').trim();
  if (raw.toLowerCase() === 'applied for') return raw;
  return raw.replace(/\s+for\s*$/i, '').trim() || raw;
}

function withReportLabel(def) {
  return { ...def, label: reportFieldLabel(def.label) };
}

/** Custom questions that get a breakdown panel + Excel sheet. */
function getPanelCustomFieldDefs(meta) {
  return (meta?.customFieldDefs || [])
    .filter(
      (def) =>
        !CUSTOM_PANEL_EXCLUDED_TYPES.includes(def.fieldType) &&
        !customFieldMatches(def, CUSTOM_PANEL_EXCLUDED_FIELDS)
    )
    .map(withReportLabel);
}

/** Custom questions that get a roster column, on screen and in the export. */
function getRosterCustomFieldDefs(meta) {
  return (meta?.customFieldDefs || [])
    .filter((def) => !customFieldMatches(def, CUSTOM_ROSTER_EXCLUDED_FIELDS))
    .map(withReportLabel);
}

/**
 * A dropdown short enough to chart in full (Ajanta's ~20-option Division) is left uncapped;
 * a long one (Burger King's 167-city list) gets the same long-tail roll-up as free text.
 * `optionCount` is 0 for anything the server could not measure, which preserves the previous
 * uncapped behaviour for every existing dropdown.
 */
const CHOICE_PANEL_MAX_OPTIONS = 30;

function isShortChoiceField(def) {
  const isChoice = def.fieldType === 'dropdown' || def.fieldType === 'dropdown_multi';
  return isChoice && (def.optionCount || 0) <= CHOICE_PANEL_MAX_OPTIONS;
}

/**
 * Burger King runs one recruiting team per region, so the team owning a screening is the
 * candidate's region. The server sends `meta.regions` only for orgs on that flag, so this
 * panel simply does not exist for anyone else.
 */
function getRegionPanel(meta) {
  if (!Array.isArray(meta?.regions) || meta.regions.length === 0) return [];
  return [
    {
      key: 'region',
      chartTitle: 'Candidates by Region',
      sheet: 'Candidates By Region',
      header: 'Region',
      extract: (c) => String(c.region || '').trim() || 'Not specified',
    },
  ];
}

/**
 * Org-configured application questions (Division / HQ / Drive Location for Ajanta MR) become breakdown
 * panels alongside the built-in ones. Driven off `meta.customFieldDefs` so no field ids
 * are hardcoded here.
 */
function getCategoryPanels(meta) {
  const region = getRegionPanel(meta);
  const used = new Set(
    [...region, ...BASE_CATEGORY_PANELS].map((panel) => panel.sheet.toLowerCase())
  );
  const custom = getPanelCustomFieldDefs(meta).map((def) => ({
    key: `custom:${def.fieldId}`,
    chartTitle: `Candidates by ${def.label}`,
    sheet: excelSheetName(`By ${def.label}`, used),
    header: def.label,
    topN: isShortChoiceField(def) ? undefined : 25,
    extract: (c) => String(c.customFields?.[def.fieldId] || '').trim() || 'Not specified',
  }));
  // Region leads, then the org questions (Division / HQ / Drive Location, or Current /
  // Preferred City), then the built-in breakdowns.
  return [...region, ...custom, ...BASE_CATEGORY_PANELS];
}

/**
 * Free-text fields (company, free-text application answers) have a long tail of one-off
 * values. Show the top
 * entries and roll the rest into a single "Others" row rather than printing 400 rows of 1.
 * Applied to the panel and the export together so the two always show the same table.
 */
function capCategoryData(data, topN, columnIds) {
  if (!data) return [];
  // The unknown bucket is exempt from the cap and always trails the "Others" roll-up.
  const unknown = data.filter((item) => isUnknownBucket(item.name));
  const known = data.filter((item) => !isUnknownBucket(item.name));
  if (!topN || known.length <= topN) return [...known, ...unknown];
  const visible = known.slice(0, topN);
  const rest = known.slice(topN);
  const byColumn = {};
  (columnIds || []).forEach((id) => {
    byColumn[id] = rest.reduce((sum, item) => sum + (item.byColumn?.[id] || 0), 0);
  });
  return [
    ...visible,
    { name: `Others (${rest.length})`, count: rest.reduce((sum, item) => sum + item.count, 0), byColumn },
    ...unknown,
  ];
}

/* ──────────────────────────────────────────────────────────────────
   DATE HELPERS
   ────────────────────────────────────────────────────────────── */

function toIsoDate(date) {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function getDefaultRange(frame) {
  const now = new Date();
  if (frame === 'week' || frame === '7d') {
    const d7 = new Date();
    d7.setDate(now.getDate() - 6);
    return { from: toIsoDate(d7), to: toIsoDate(endOfDay(now)) };
  }
  if (frame === 'month' || frame === '1m') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: toIsoDate(first), to: toIsoDate(endOfDay(now)) };
  }
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: toIsoDate(first), to: toIsoDate(endOfDay(now)) };
}

function formatDateLabel(value) {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatMonthLabel(value) {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function formatExportDate(value) {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function compactDate(value) {
  if (!value) return '';
  return String(value).slice(0, 10).replace(/-/g, '');
}

function slugFilePart(value, fallback = 'report', maxLength = 44) {
  const cleaned = String(value || fallback)
    .trim()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, maxLength)
    .replace(/-+$/g, '');
  return cleaned || fallback;
}

function getReportFileBaseName(report) {
  const uniqueJobs = new Set((report.rawCandidates || []).map(c => c.jobTitle).filter(Boolean));
  let jobStr = 'report';

  if (uniqueJobs.size > 1) {
    jobStr = `${uniqueJobs.size}-Jobs`;
  } else if (uniqueJobs.size === 1) {
    jobStr = slugFilePart(Array.from(uniqueJobs)[0], 'job', 30);
  } else if (report.meta.jobTitle) {
    jobStr = slugFilePart(report.meta.jobTitle, 'job', 30);
  }

  const from = compactDate(report.meta.from);
  const to = compactDate(report.meta.to);
  const range = from && to ? `${from}-to-${to}` : compactDate(new Date().toISOString());
  return `TalentCrux_Report_${jobStr}_${range}`;
}

/* ──────────────────────────────────────────────────────────────────
   DATA HELPERS
   ────────────────────────────────────────────────────────────── */

function normalizeReportPayload(payload, fallbackContext) {
  if (!payload || typeof payload !== 'object') return null;
  const fallback = {
    meta: {
      jobId: '',
      jobTitle: '',
      jobs: [],
      screeningId: '',
      screeningName: '',
      from: fallbackContext?.from || '',
      to: fallbackContext?.to || '',
      previousLabel: 'previous period',
    },
    summary: {},
    comparison: {},
    outcomeBreakdown: [],
    scoreDistribution: [],
    funnel: [],
    locationBreakdown: [],
    applicationsOverTime: [],
    rawCandidates: [],
  };
  return {
    ...fallback,
    ...payload,
    meta: { ...fallback.meta, ...(payload.meta || {}) },
    summary: { ...fallback.summary, ...(payload.summary || {}) },
    comparison: { ...fallback.comparison, ...(payload.comparison || {}) },
  };
}

/**
 * What to tell the user when a report request fails. 403 messages are written for people
 * ("You are not assigned to a region…"); a 404 means the selection went stale (a job moved team,
 * a screening was deleted) and a refresh rebuilds it.
 */
function getApiErrorMessage(error, fallback) {
  const status = error?.response?.status;
  const message = error?.response?.data?.message;
  if (status === 403 && message) return message;
  if (status === 404) {
    return 'Some of the selected job posts or screenings are no longer available to you. Refresh the page and try again.';
  }
  return fallback;
}

function reportMatchesParams(reportData, params) {
  if (!reportData?.meta || !params) return false;
  return (
    JSON.stringify(reportData.meta.jobId) === JSON.stringify(params.jobId) &&
    JSON.stringify(reportData.meta.screeningId) === JSON.stringify(params.screeningId) &&
    String(reportData.meta.from) === String(params.from) &&
    String(reportData.meta.to) === String(params.to)
  );
}

/* ──────────────────────────────────────────────────────────────────
   PER-COLUMN BREAKDOWN COMPUTATION
   ────────────────────────────────────────────────────────────── */

function buildCategoryDataFromMap(perJobMap, jobs, sort = true) {
  const allNames = new Set();
  Object.values(perJobMap).forEach((map) => {
    Object.keys(map).forEach((name) => allNames.add(name));
  });
  const data = Array.from(allNames)
    .map((name) => {
      const total = Object.values(perJobMap).reduce((sum, map) => sum + (map[name] || 0), 0);
      const byColumn = {};
      jobs.forEach((job) => {
        byColumn[job.id] = perJobMap[job.id]?.[name] || 0;
      });
      return { name, count: total, byColumn };
    });

  if (sort) {
    data.sort(compareCategoryRows);
  }
  return data;
}

function getExperienceBand(years) {
  if (years == null || years === '') return 'Unknown';
  const y = Number(years);
  if (isNaN(y)) return 'Unknown';
  if (y < 2) return '0-2 years';
  if (y < 5) return '2-5 years';
  if (y < 10) return '5-10 years';
  return '10+ years';
}

/**
 * "Mark as Dropped" is gated to Go Colors on the screening page, and no org on this report
 * has it, so `Dropped` would otherwise be a permanent 0 column that only confuses readers.
 * Gate it on the data rather than on the org, so a drop that does land — via import or the
 * API — is never hidden. Same rule the server already applies to `outcomeBreakdown`, which
 * omits zero-count outcomes. Checks both sources: either seeing a drop is enough.
 */
function hasDroppedOutcome(report) {
  if (Number(report?.summary?.dropped || 0) > 0) return true;
  return (report?.rawCandidates || []).some((c) => c.outcome === 'Dropped');
}

/**
 * Outcome rows for every table, sheet and chart in the page, built from this one list so no
 * two of them can disagree about whether `Dropped` is shown.
 */
function getOutcomeKeys(report) {
  return hasDroppedOutcome(report)
    ? ['Selected', 'Rejected', 'Dropped', 'Undecided']
    : ['Selected', 'Rejected', 'Undecided'];
}

/**
 * The funnel's middle stages depend on what the screening actually runs. Ajanta MR is
 * assessment-only, so a hardcoded "Interviews" stage reads 0 for every candidate. The
 * closing "Dropped" stage follows the same rule via `hasDroppedOutcome`.
 * Every funnel in the page and the export is built from this one list so they agree.
 */
function getFunnelStages(report) {
  const meta = report?.meta;
  const stages = [{ key: 'applied', label: 'Applied' }];
  if (meta?.hasAssessment) stages.push({ key: 'assessmentsDone', label: 'Assessment' });
  if (meta?.hasInterview) stages.push({ key: 'interviewsDone', label: 'Interviews' });
  stages.push({ key: 'selected', label: 'Selected' });
  if (hasDroppedOutcome(report)) stages.push({ key: 'dropped', label: 'Dropped' });
  return stages;
}

/** Whole-report stage counts, using the same definitions as the per-column breakdowns. */
function countFunnelStages(report, stages) {
  const candidates = report.rawCandidates || [];
  const totals = {
    applied: report.summary.totalCandidates || 0,
    assessmentsDone: candidates.filter((c) => c.assessmentScore != null).length,
    interviewsDone: candidates.filter((c) => c.interviewScore != null).length,
    selected: report.summary.selected || 0,
    dropped: report.summary.dropped || 0,
  };
  return stages.map((stage) => ({ key: stage.key, label: stage.label, count: totals[stage.key] || 0 }));
}

/**
 * Candidates type their employer freehand, so "Alkem Laboratories", "ALKEM LTD" and
 * "Alkem Laboratories Ltd." arrive as three companies. Group on a stripped-down key and
 * display the spelling that occurs most often. Raw values are untouched in the roster.
 */
const COMPANY_NOISE_WORDS = /\b(pvt|private|ltd|limited|llp|inc|corp|corporation|co|company|laboratories|laboratory|labs|lab|pharmaceuticals|pharmaceutical|pharma|healthcare|health|industries|india|group)\b/g;

function companyGroupKey(name) {
  return String(name || '')
    .toLowerCase()
    // Curly quotes matter: "Dr Reddy's" and "Dr. Reddy’s" must land in the same bucket.
    .replace(/[.,&'"`´’‘“”()-]/g, ' ')
    .replace(COMPANY_NOISE_WORDS, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildCompanyLabeller(rawCandidates) {
  const spellings = new Map(); // key -> Map(originalSpelling -> count)
  (rawCandidates || []).forEach((c) => {
    const raw = String(c.currentCompany || '').trim();
    if (!raw) return;
    const key = companyGroupKey(raw);
    if (!key) return;
    if (!spellings.has(key)) spellings.set(key, new Map());
    const bucket = spellings.get(key);
    bucket.set(raw, (bucket.get(raw) || 0) + 1);
  });

  const canonical = new Map();
  spellings.forEach((bucket, key) => {
    // Most frequent spelling wins; ties go to the longer (usually more complete) name.
    const best = Array.from(bucket.entries()).sort(
      (a, b) => b[1] - a[1] || b[0].length - a[0].length
    )[0];
    canonical.set(key, best[0]);
  });

  return (candidate) => {
    const raw = String(candidate.currentCompany || '').trim();
    if (!raw) return 'Unknown';
    const key = companyGroupKey(raw);
    if (!key) return raw;
    return canonical.get(key) || raw;
  };
}

/**
 * Breakdowns keyed by an arbitrary column axis.
 *
 * The MR/GoColours pages always group by job post. BK groups by ROLE (so the nine regional
 * copies of a role sum into one column) or by REGION, so the axis is a parameter: `columns` is
 * the `[{ id, title }]` list to build, and `getColumnId(candidate)` says where each candidate
 * lands. Candidates whose column is not in `columns` are skipped, exactly as an out-of-scope
 * job post was skipped before.
 */
function computePerColumnBreakdowns(rawCandidates, columns, panels, getColumnId) {
  if (!rawCandidates?.length || !columns?.length || typeof getColumnId !== 'function') return null;

  const activePanels = panels || BASE_CATEGORY_PANELS;
  const ctx = { labelCompany: buildCompanyLabeller(rawCandidates) };

  const outcomes = {};
  const funnel = {};
  const timeline = {};
  const byPanel = {};

  columns.forEach((column) => {
    outcomes[column.id] = { Selected: 0, Rejected: 0, Dropped: 0, Undecided: 0 };
    funnel[column.id] = { applied: 0, assessmentsDone: 0, interviewsDone: 0, selected: 0, dropped: 0 };
    timeline[column.id] = {};
  });
  activePanels.forEach((panel) => {
    byPanel[panel.key] = {};
    columns.forEach((column) => {
      byPanel[panel.key][column.id] = {};
    });
  });

  rawCandidates.forEach((c) => {
    const cid = getColumnId(c);
    if (!cid || !outcomes[cid]) return;

    const outcome = c.outcome || 'Undecided';
    if (outcomes[cid][outcome] !== undefined) outcomes[cid][outcome] += 1;
    else outcomes[cid].Undecided += 1;

    funnel[cid].applied += 1;
    if (c.assessmentScore != null) funnel[cid].assessmentsDone += 1;
    if (c.interviewScore != null) funnel[cid].interviewsDone += 1;
    if (outcome === 'Selected') funnel[cid].selected += 1;
    if (outcome === 'Dropped') funnel[cid].dropped += 1;

    activePanels.forEach((panel) => {
      const value = panel.extract(c, ctx);
      if (value) byPanel[panel.key][cid][value] = (byPanel[panel.key][cid][value] || 0) + 1;
    });

    const date = c.appliedDate;
    if (date) timeline[cid][date] = (timeline[cid][date] || 0) + 1;
  });

  const categories = {};
  activePanels.forEach((panel) => {
    categories[panel.key] = buildCategoryDataFromMap(byPanel[panel.key], columns);
  });

  return { outcomes, funnel, categories, timeline };
}

function getMonthsInRange(fromStr, toStr) {
  if (!fromStr || !toStr) return [];
  const start = new Date(fromStr);
  const end = new Date(toStr);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];

  const months = [];
  const current = new Date(start.getFullYear(), start.getMonth(), 1);
  const lastMonth = new Date(end.getFullYear(), end.getMonth(), 1);

  while (current <= lastMonth) {
    const yyyy = current.getFullYear();
    const mm = String(current.getMonth() + 1).padStart(2, '0');
    const monthId = `${yyyy}-${mm}`;
    const monthTitle = current.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    months.push({ id: monthId, title: monthTitle });
    current.setMonth(current.getMonth() + 1);
  }

  return months;
}

function computePerMonthBreakdowns(rawCandidates, months, panels) {
  if (!rawCandidates?.length || !months?.length) return null;

  const activePanels = panels || BASE_CATEGORY_PANELS;
  const ctx = { labelCompany: buildCompanyLabeller(rawCandidates) };

  const outcomes = {};
  const funnel = {};
  const byPanel = {};

  months.forEach((m) => {
    outcomes[m.id] = { Selected: 0, Rejected: 0, Dropped: 0, Undecided: 0 };
    funnel[m.id] = { applied: 0, assessmentsDone: 0, interviewsDone: 0, selected: 0, dropped: 0 };
  });
  activePanels.forEach((panel) => {
    byPanel[panel.key] = {};
    months.forEach((m) => {
      byPanel[panel.key][m.id] = {};
    });
  });

  rawCandidates.forEach((c) => {
    const dateVal = c.appliedDate || c.appliedAt;
    if (!dateVal) return;
    const monthId = String(dateVal).slice(0, 7);
    if (!outcomes[monthId]) return;

    // Outcomes
    const outcome = c.outcome || 'Undecided';
    if (outcomes[monthId][outcome] !== undefined) {
      outcomes[monthId][outcome] += 1;
    } else {
      outcomes[monthId].Undecided += 1;
    }

    // Funnel
    funnel[monthId].applied += 1;
    if (c.assessmentScore != null) funnel[monthId].assessmentsDone += 1;
    if (c.interviewScore != null) funnel[monthId].interviewsDone += 1;
    if (outcome === 'Selected') funnel[monthId].selected += 1;
    if (outcome === 'Dropped') funnel[monthId].dropped += 1;

    // Categories
    activePanels.forEach((panel) => {
      const value = panel.extract(c, ctx);
      if (value) byPanel[panel.key][monthId][value] = (byPanel[panel.key][monthId][value] || 0) + 1;
    });
  });

  const categories = {};
  activePanels.forEach((panel) => {
    categories[panel.key] = buildCategoryDataFromMap(byPanel[panel.key], months);
  });

  return { outcomes, funnel, categories };
}

/** Build single-column category data from rawCandidates for each panel's extractor. */
function computeSingleColumnCategories(rawCandidates, panels) {
  if (!rawCandidates?.length) return null;
  const ctx = { labelCompany: buildCompanyLabeller(rawCandidates) };
  const categories = {};

  (panels || BASE_CATEGORY_PANELS).forEach((panel) => {
    const counts = new Map();
    rawCandidates.forEach((c) => {
      const value = panel.extract(c, ctx);
      if (value) counts.set(value, (counts.get(value) || 0) + 1);
    });
    categories[panel.key] = Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort(compareCategoryRows);
  });

  return categories;
}



/* ──────────────────────────────────────────────────────────────────
   SHARED UI COMPONENTS
   ────────────────────────────────────────────────────────────── */

function Panel({ title, eyebrow, children, className, headerRight }) {
  return (
    <section className={cn('min-w-0 rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm transition-shadow hover:shadow-md h-full flex flex-col', className)}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
          <h2 className="text-base font-bold leading-snug text-slate-950">{title}</h2>
          {eyebrow && <span className="shrink-0 rounded-full bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-500">{eyebrow}</span>}
        </div>
        {headerRight && <div className="ml-auto shrink-0">{headerRight}</div>}
      </div>
      <div className="flex-1 min-h-0 min-w-0">{children}</div>
    </section>
  );
}

function ViewToggle({ isActive, onOpenVisual }) {
  return (
    <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200/50">
      <button
        type="button"
        onClick={() => { }}
        className={cn('px-3 py-1 text-[11px] font-semibold rounded-md transition-all', !isActive ? 'bg-white shadow-sm text-[#007BFF]' : 'text-slate-500 hover:text-slate-700 cursor-default')}
      >
        Data
      </button>
      <button
        type="button"
        onClick={onOpenVisual}
        className={cn('px-3 py-1 text-[11px] font-semibold rounded-md transition-all', isActive ? 'bg-white shadow-sm text-[#007BFF]' : 'text-slate-500 hover:text-slate-700 cursor-pointer')}
      >
        Visual
      </button>
    </div>
  );
}

function SummaryStrip({ summary, showDropped }) {
  const items = [
    { color: null, value: summary.totalCandidates, label: 'Total' },
    { color: OUTCOME_COLORS.Selected, value: summary.selected, label: `Selected (${summary.selectionRate}%)` },
    ...(showDropped ? [{ color: OUTCOME_COLORS.Dropped, value: summary.dropped || 0, label: 'Dropped' }] : []),
    { color: OUTCOME_COLORS.Rejected, value: summary.rejected, label: 'Rejected' },
    { color: OUTCOME_COLORS.Undecided, value: summary.undecided ?? (summary.totalCandidates - summary.selected - summary.rejected), label: 'Undecided' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-slate-100 pb-4 mb-4 shrink-0">
      {items.map((item, i) => (
        <React.Fragment key={item.label}>
          {i > 0 && <div className="hidden sm:block h-5 w-px bg-slate-200" />}
          <div className="flex items-center gap-1.5">
            {item.color && <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: item.color }} />}
            <span className={cn('font-black tabular-nums', i === 0 ? 'text-2xl text-slate-950' : 'text-lg text-slate-950')}>{item.value}</span>
            <span className="text-xs text-slate-500">{item.label}</span>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function EmptyReportState({ isLoading, errorMessage }) {
  if (errorMessage && !isLoading) {
    return (
      <section className="flex min-h-[460px] items-center justify-center rounded-2xl border border-dashed border-amber-300 bg-white px-6 py-16 text-center shadow-sm">
        <div className="max-w-md">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
            <BarChart3 className="h-5 w-5" />
          </div>
          <h2 className="mt-4 text-base font-black text-slate-950">The report could not be generated.</h2>
          <p className="mt-2 text-sm text-slate-500">{errorMessage}</p>
        </div>
      </section>
    );
  }
  return (
    <section className="flex min-h-[460px] items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center shadow-sm">
      <div className="max-w-md">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-[#007BFF]">
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <BarChart3 className="h-5 w-5" />}
        </div>
        <h2 className="mt-4 text-base font-black text-slate-950">{isLoading ? 'Generating report' : 'Select one or more regions to generate a report.'}</h2>
        {isLoading && <p className="mt-2 text-sm text-slate-500">We are preparing the hiring report for the selected filters.</p>}
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
   OUTCOME PANEL (DATA ONLY)
   ────────────────────────────────────────────────────────────── */

function OutcomeComparisonTable({ columns, perColumnOutcomes, outcomeKeys }) {
  const cols = columns || [];
  const outcomes = perColumnOutcomes || {};
  return (
    <div className="overflow-x-auto minimal-scrollbar">
      <table className="report-sticky-first min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2.5 text-left">Outcome</th>
            {cols.map((col) => (
              <th key={col.id} className="px-3 py-2.5 text-center min-w-[96px] max-w-[180px] leading-snug">{col.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {outcomeKeys.map((outcome) => (
            <tr key={outcome} className="border-b border-slate-100 transition-colors hover:bg-slate-50/50">
              <td className="px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: OUTCOME_COLORS[outcome] || '#94a3b8' }} />
                  <span className="font-medium text-slate-700">{outcome}</span>
                </div>
              </td>
              {cols.map((col) => (
                <td key={col.id} className="px-3 py-2.5 text-center font-semibold text-slate-950 tabular-nums">{outcomes[col.id]?.[outcome] || 0}</td>
              ))}
            </tr>
          ))}
          <tr className="border-t-2 border-slate-200 bg-slate-50/60">
            <td className="px-3 py-2.5 font-bold text-slate-950">Total</td>
            {cols.map((col) => {
              const total = outcomeKeys.reduce((sum, o) => sum + (outcomes[col.id]?.[o] || 0), 0);
              return <td key={col.id} className="px-3 py-2.5 text-center font-black text-slate-950 tabular-nums">{total}</td>;
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function OutcomePanel({ report, isMultiColumnMode, columns, breakdownOutcomes, activeModal, setActiveModal }) {
  const cols = columns || [];
  const showMulti = isMultiColumnMode && cols.length > 0;
  return (
    <Panel
      title="Outcome breakdown"
      eyebrow={showMulti ? `${cols.length} columns` : 'by recruiter decision'}
      className="lg:col-span-6"
      headerRight={<ViewToggle isActive={activeModal === 'outcome'} onOpenVisual={() => setActiveModal('outcome')} />}
    >
      <SummaryStrip summary={report.summary} showDropped={hasDroppedOutcome(report)} />

      {showMulti && breakdownOutcomes ? <OutcomeComparisonTable columns={cols} perColumnOutcomes={breakdownOutcomes} outcomeKeys={getOutcomeKeys(report)} /> : (
        <div className="mt-4">
          <div className="flex h-8 w-full overflow-hidden rounded-lg bg-slate-100">
            {report.outcomeBreakdown.map((item) => (
              <div key={item.name} className="flex items-center justify-center text-xs font-bold text-white transition-all" style={{ width: `${Math.max(item.pct, 2)}%`, backgroundColor: OUTCOME_COLORS[item.name] || '#94a3b8' }}>
                {item.pct >= 10 ? `${item.pct}%` : ''}
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2.5">
            {report.outcomeBreakdown.map((item) => (
              <div key={item.name} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2.5">
                  <span className="h-3 w-3 rounded" style={{ backgroundColor: OUTCOME_COLORS[item.name] || '#94a3b8' }} />
                  <span className="font-medium text-slate-700">{item.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <strong className="text-slate-950 tabular-nums">{item.value}</strong>
                  <span className="w-12 text-right text-xs text-slate-400">{item.pct}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}

/* ──────────────────────────────────────────────────────────────────
   FUNNEL PANEL (DATA ONLY)
   ────────────────────────────────────────────────────────────── */

function FunnelPanel({ report, isMultiColumnMode, columns, breakdownFunnel, activeModal, setActiveModal }) {
  const cols = columns || [];
  const showMulti = isMultiColumnMode && cols.length > 0;
  const funnelMap = breakdownFunnel || {};

  const stages = useMemo(() => getFunnelStages(report), [report]);
  const singleStages = useMemo(() => countFunnelStages(report, stages), [report, stages]);

  return (
    <Panel
      title="Hiring funnel"
      eyebrow={showMulti ? `${cols.length} columns` : 'stage progression'}
      className="lg:col-span-6"
      headerRight={<ViewToggle isActive={activeModal === 'funnel'} onOpenVisual={() => setActiveModal('funnel')} />}
    >
      <div className="h-full flex flex-col justify-center">
        {showMulti ? (
          <div className="overflow-x-auto minimal-scrollbar">
            <table className="report-sticky-first min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2.5 text-left">Stage</th>
                  {cols.map((col) => (
                    <th key={col.id} className="px-3 py-2.5 text-center min-w-[96px] max-w-[180px] leading-snug">{col.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stages.map((stage, si) => (
                  <tr key={stage.key} className={cn('border-b border-slate-100 transition-colors hover:bg-slate-50/50', si === stages.length - 1 && 'border-b-0 bg-green-50/40')}>
                    <td className="px-3 py-2.5 font-medium text-slate-700">{stage.label}</td>
                    {cols.map((col) => (
                      <td key={col.id} className="px-3 py-2.5 text-center font-semibold text-slate-950 tabular-nums">{funnelMap[col.id]?.[stage.key] || 0}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="space-y-4">
            {singleStages.map((item, i) => (
              <div key={item.label} className={cn('flex items-center justify-between rounded-lg px-4 py-3', i === singleStages.length - 1 ? 'bg-green-50' : 'bg-slate-50')}>
                <span className="text-sm font-medium text-slate-700">{item.label}</span>
                <strong className="text-xl font-black text-slate-950 tabular-nums">{item.count}</strong>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}

function CategoryPanel({ title, data, isMultiColumnMode, columns, panelId, activeModal, setActiveModal }) {
  if (!data || !data.length) return null;
  const cols = columns || [];
  const showMulti = isMultiColumnMode && cols.length > 0;
  const firstColHeader = title.replace(/^Candidates (by|per) /i, '').trim();

  return (
    <Panel
      title={title}
      eyebrow={`${data.length} entries`}
      headerRight={setActiveModal ? <ViewToggle isActive={activeModal === panelId} onOpenVisual={() => setActiveModal(panelId)} /> : null}
    >
      {!showMulti ? (
        <div className="space-y-1 max-h-[300px] overflow-y-auto minimal-scrollbar pr-1 relative">
          {data.map((item, i) => (
            <div key={item.name} className={cn('flex items-center justify-between rounded-md px-3 py-2 text-sm', i % 2 === 0 ? 'bg-slate-50/60' : '')}>
              <span className="truncate font-medium text-slate-700 mr-4">{item.name || '—'}</span>
              <strong className="shrink-0 text-slate-950 tabular-nums">{item.count}</strong>
            </div>
          ))}
        </div>
      ) : (
        <div className="max-h-[300px] overflow-auto minimal-scrollbar relative">
          <table className="min-w-full text-sm border-separate border-spacing-0">
            <thead>
              <tr className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                <th className="sticky top-0 left-0 z-30 bg-white px-3 py-2 text-left shadow-[1px_1px_0_0_#e2e8f0]">{firstColHeader}</th>
                {cols.map((col) => (
                  <th key={col.id} className="sticky top-0 z-20 bg-white px-3 py-2 text-center min-w-[96px] max-w-[160px] leading-snug shadow-[0_1px_0_0_#e2e8f0]">{col.title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item, ri) => (
                <tr key={item.name} className={cn('group transition-colors hover:bg-slate-50/50', ri % 2 === 0 ? 'bg-slate-50/30' : '')}>
                  <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-3 py-2 font-medium text-slate-700 truncate max-w-[160px] border-b border-slate-50 shadow-[1px_0_0_0_#e2e8f0]">{item.name || '—'}</td>
                  {cols.map((col) => (
                    <td key={col.id} className="px-3 py-2 text-center font-semibold text-slate-950 tabular-nums border-b border-slate-50">{item.byColumn?.[col.id] || 0}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

  /* ──────────────────────────────────────────────────────────────────
     VISUAL MODAL & CHART COMPONENTS
     ────────────────────────────────────────────────────────────── */

  function CustomTabularLegend({ payload, total }) {
    if (!payload || !payload.length) return null;
    return (
      <div className="w-full flex justify-center">
        <table className="text-sm text-slate-700 w-full max-w-sm">
          <tbody>
            {payload.map((entry, index) => {
              const count = entry.payload?.value || entry.payload?.count || entry.payload?.payload?.value || entry.payload?.payload?.count || 0;
              const pct = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
              return (
                <tr key={`legend-row-${index}`} className="border-b border-slate-100 last:border-none">
                  <td className="py-2 pl-2 w-8"><div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} /></td>
                  <td className="py-2 font-medium">{entry.value}</td>
                  <td className="py-2 text-right font-bold tabular-nums">{count}</td>
                  <td className="py-2 pr-2 text-right text-slate-500 tabular-nums w-20">({pct}%)</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t-2 border-slate-200">
            <tr>
              <td colSpan={2} className="py-3 pl-2 font-bold text-slate-900">Total</td>
              <td className="py-3 text-right font-bold text-slate-900 tabular-nums">{total}</td>
              <td className="py-3 pr-2 text-right text-slate-500 tabular-nums">(100%)</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  function DonutChartLayout({ data, dataKey, nameKey, colors, title }) {
    const total = data.reduce((sum, item) => sum + (item[dataKey] || 0), 0);

    return (
      <div className="flex flex-col h-full w-full pt-4">
        {title && <h4 className="text-lg font-bold text-slate-900 mb-6 truncate w-full text-center px-4">{title}</h4>}

        <div className="flex flex-col lg:flex-row w-full h-full items-center justify-center gap-6 lg:gap-10">

          <div className="relative w-full lg:w-1/2 h-[260px] lg:h-[350px] flex-shrink-0 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey={dataKey} nameKey={nameKey} cx="50%" cy="50%" innerRadius="55%" outerRadius="85%" paddingAngle={2} isAnimationActive={true}>
                  {data.map((entry, i) => (
                    <Cell key={entry[nameKey]} fill={colors[entry[nameKey]] || colors[i % (colors.length || 1)] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} formatter={(value, name) => [`${value}`, name]} />
              </PieChart>
            </ResponsiveContainer>

            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-sm font-semibold text-slate-500">Total</span>
              <span className="text-3xl font-black text-slate-900 leading-tight">{total}</span>
              <span className="text-xs font-medium text-slate-500">Candidates</span>
            </div>
          </div>

          <div className="w-full lg:w-1/2 flex items-center justify-center">
            <CustomTabularLegend
              payload={data.map((d, i) => ({
                color: colors[d[nameKey]] || colors[i % (colors.length || 1)] || '#94a3b8',
                value: d[nameKey],
                payload: { value: d[dataKey] }
              }))}
              total={total}
            />
          </div>
        </div>
      </div>
    );
  }

  function OutcomeVisualSingle({ outcomeBreakdown }) {
    return <DonutChartLayout data={outcomeBreakdown} dataKey="value" nameKey="name" colors={OUTCOME_COLORS} />;
  }

  function FunnelVisualSingle({ stages }) {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={stages} margin={{ top: 20, right: 30, left: 20, bottom: 20 }} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#edf2f7" />
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="label" tick={{ fontSize: 14, fill: '#475569' }} axisLine={false} tickLine={false} width={120} />
          <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
          <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} isAnimationActive={true} barSize={48} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  function FunnelVisualMulti({ columns, perColumnFunnel, stages }) {
    const chartData = (stages || []).map(({ key, label }) => {
      const datum = { stage: label };
      let total = 0;
      columns.forEach(col => {
        const count = perColumnFunnel[col.id]?.[key] || 0;
        datum[col.title] = count;
        total += count;
      });
      datum.total = total;
      return datum;
    });

    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 40, right: 30, left: 20, bottom: 20 }} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#edf2f7" />
          <XAxis type="number" tick={{ fontSize: 13, fill: '#64748b' }} axisLine={false} tickLine={false} />
          <YAxis type="category" dataKey="stage" tick={{ fontSize: 14, fill: '#475569', fontWeight: 600 }} axisLine={false} tickLine={false} width={120} />
          <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
          <Legend verticalAlign="top" height={60} iconType="square" wrapperStyle={{ fontSize: 14, paddingBottom: 20 }} />
          {columns.map((col, i) => (
            <Bar key={col.id} dataKey={col.title} stackId="a" fill={JOB_COLORS[i % JOB_COLORS.length]} isAnimationActive={true} barSize={48}>
              <LabelList dataKey={col.title} position="inside" fill="#fff" fontSize={12} formatter={(val) => val > 0 ? val : ''} />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  function CategoryVisualSingle({ data }) {
    const topN = data.slice(0, CHART_MAX_CATEGORIES);
    const others = data.slice(CHART_MAX_CATEGORIES);
    const chartData = [...topN];
    if (others.length > 0) {
      chartData.push({ name: `Other (${others.length})`, count: others.reduce((sum, item) => sum + item.count, 0) });
    }

    return <DonutChartLayout data={chartData} dataKey="count" nameKey="name" colors={CATEGORY_COLORS} />;
  }

  function CategoryVisualMulti({ data, columns }) {
    const topN = data.slice(0, CHART_MAX_CATEGORIES);
    const others = data.slice(CHART_MAX_CATEGORIES);

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full h-full overflow-y-auto minimal-scrollbar p-4 content-start">
        {columns.map((col) => {
          const chartData = [...topN].map(cat => ({ name: cat.name, count: cat.byColumn?.[col.id] || 0 }));
          if (others.length > 0) {
            chartData.push({ name: `Other (${others.length})`, count: others.reduce((sum, cat) => sum + (cat.byColumn?.[col.id] || 0), 0) });
          }
          const filteredData = chartData.filter(d => d.count > 0);

          return (
            <div key={col.id} className="flex min-h-[400px] flex-col border border-slate-100 rounded-xl shadow-sm bg-slate-50/50 p-4">
              <h4 className="mb-1 w-full truncate text-center text-base font-bold text-slate-900">{col.title}</h4>
              <div className="min-h-0 flex-1">
                <DonutChartLayout data={filteredData} dataKey="count" nameKey="name" colors={CATEGORY_COLORS} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function VisualModal({ activeModal, onClose, report, isMultiColumnMode, columns, breakdownData, categoryData }) {
    if (!activeModal || !report) return null;

    const cols = columns || [];
    let title = '';
    let content = null;

    if (activeModal === 'outcome') {
      title = 'Outcome Breakdown';
      content = <OutcomeVisualSingle outcomeBreakdown={report.outcomeBreakdown} />;
    } else if (activeModal === 'funnel') {
      title = 'Hiring Funnel';
      const funnelStages = getFunnelStages(report);
      content = isMultiColumnMode
        ? <FunnelVisualMulti columns={cols} perColumnFunnel={breakdownData?.funnel || {}} stages={funnelStages} />
        : <FunnelVisualSingle stages={countFunnelStages(report, funnelStages)} />;
    } else {
      title = getCategoryPanels(report.meta).find((panel) => panel.key === activeModal)?.chartTitle || 'Candidates';
      const data = categoryData?.[activeModal] || [];
      content = isMultiColumnMode
        ? <CategoryVisualMulti data={data} columns={cols} />
        : <CategoryVisualSingle data={data} />;
    }

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4 border-b border-slate-100 bg-slate-50">
            <h3 className="text-lg font-black tracking-tight text-slate-900">{title} — Detailed Visualization</h3>
            <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200/50 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-3 sm:p-6 bg-white w-full h-[70vh] sm:h-[600px] overflow-y-auto">
            {content}
          </div>
        </div>
      </div>
    );
  }

  /* ──────────────────────────────────────────────────────────────────
     TIMELINE PANEL
     ────────────────────────────────────────────────────────────── */

  function TimelinePanel({ report, isMultiColumnMode, columns, perColumnData, comparisonMode, monthColumns, columnLabel }) {
    const [chartType, setChartType] = useState('area'); // 'area' or 'bar'
    const cols = columns || [];
    const isMonthMode = comparisonMode === 'month';
    // In "By Month" mode the x-axis is the month itself, so the per-column series collapse.
    const showColumnSeries = !isMonthMode && isMultiColumnMode;

    const timelineData = useMemo(() => {
      if (isMonthMode) {
        const counts = {};
        (report.rawCandidates || []).forEach((c) => {
          const dateVal = c.appliedDate || c.appliedAt;
          if (!dateVal) return;
          const mId = String(dateVal).slice(0, 7);
          counts[mId] = (counts[mId] || 0) + 1;
        });
        return (monthColumns || []).map((m) => ({ date: m.id, label: m.title, count: counts[m.id] || 0 }));
      }
      if (!isMultiColumnMode || !perColumnData?.timeline) return report.applicationsOverTime;
      const allDates = new Set();
      Object.values(perColumnData.timeline).forEach((dateMap) => Object.keys(dateMap).forEach((date) => allDates.add(date)));
      return Array.from(allDates).sort().map((date) => {
        const point = { date, label: formatDateLabel(date) };
        cols.forEach((column) => { point[column.id] = perColumnData.timeline[column.id]?.[date] || 0; });
        return point;
      });
    }, [isMonthMode, monthColumns, isMultiColumnMode, perColumnData, report.applicationsOverTime, report.rawCandidates, cols]);

    return (
      <Panel
        title="Applications over time"
        eyebrow={
          isMonthMode
            ? `${(monthColumns || []).length} months`
            : isMultiColumnMode
              ? `${cols.length} ${String(columnLabel || 'column').toLowerCase()}s`
              : formatMonthLabel(report.meta.from) || 'selected period'
        }
        headerRight={
          <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200/50">
            <button type="button" onClick={() => setChartType('area')} className={cn('px-3 py-1 text-[11px] font-semibold rounded-md transition-all', chartType === 'area' ? 'bg-white shadow-sm text-[#007BFF]' : 'text-slate-500 hover:text-slate-700')}>Line</button>
            <button type="button" onClick={() => setChartType('bar')} className={cn('px-3 py-1 text-[11px] font-semibold rounded-md transition-all', chartType === 'bar' ? 'bg-white shadow-sm text-[#007BFF]' : 'text-slate-500 hover:text-slate-700')}>Bar</button>
          </div>
        }
      >
        <div className="h-[240px] mt-2">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'area' ? (
              <AreaChart data={timelineData} margin={{ top: 16, right: 12, left: -18, bottom: 0 }}>
                <defs>
                  {showColumnSeries ? (
                    cols.map((column, i) => {
                      const safeId = `grad-${column.id.replace(/[^a-zA-Z0-9_-]/g, '')}`;
                      return (
                        <linearGradient key={column.id} id={safeId} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={JOB_COLORS[i % JOB_COLORS.length]} stopOpacity={0.18} />
                          <stop offset="95%" stopColor={JOB_COLORS[i % JOB_COLORS.length]} stopOpacity={0.02} />
                        </linearGradient>
                      );
                    })
                  ) : (
                    <linearGradient id="reportApplications" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1683f8" stopOpacity={0.28} />
                      <stop offset="95%" stopColor="#1683f8" stopOpacity={0.02} />
                    </linearGradient>
                  )}
                </defs>
                <CartesianGrid stroke="#edf2f7" vertical={false} />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis hide />
                <Tooltip contentStyle={{ borderRadius: 10, borderColor: '#dbe4ef', boxShadow: '0 12px 28px rgba(15,23,42,0.12)' }} />
                {showColumnSeries ? (
                  cols.map((column, i) => {
                    const safeId = `grad-${column.id.replace(/[^a-zA-Z0-9_-]/g, '')}`;
                    return (
                      <Area key={column.id} type="monotone" dataKey={column.id} name={column.title} stroke={JOB_COLORS[i % JOB_COLORS.length]} strokeWidth={2} fill={`url(#${safeId})`} fillOpacity={1} isAnimationActive={true} />
                    );
                  })
                ) : (
                  <Area type="monotone" dataKey="count" stroke="#007BFF" strokeWidth={3} fill="url(#reportApplications)" isAnimationActive={true} />
                )}
              </AreaChart>
            ) : (
              <BarChart data={timelineData} margin={{ top: 16, right: 12, left: -18, bottom: 0 }}>
                <CartesianGrid stroke="#edf2f7" vertical={false} />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                <YAxis hide />
                <Tooltip contentStyle={{ borderRadius: 10, borderColor: '#dbe4ef', boxShadow: '0 12px 28px rgba(15,23,42,0.12)' }} cursor={{ fill: '#f1f5f9' }} />
                {showColumnSeries ? (
                  cols.map((column, i) => (
                    <Bar key={column.id} dataKey={column.id} name={column.title} fill={JOB_COLORS[i % JOB_COLORS.length]} radius={[4, 4, 0, 0]} isAnimationActive={true} />
                  ))
                ) : (
                  <Bar dataKey="count" fill="#007BFF" radius={[4, 4, 0, 0]} isAnimationActive={true} />
                )}
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
        {showColumnSeries && cols.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 border-t border-slate-100 pt-3">
            {cols.map((column, i) => (
              <div key={column.id} className="flex items-center gap-1.5 text-xs text-slate-600">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: JOB_COLORS[i % JOB_COLORS.length] }} />
                <span className="truncate max-w-[160px]">{column.title}</span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    );
  }

  /* ──────────────────────────────────────────────────────────────────
     ROSTER PANEL (raw data table)
     ────────────────────────────────────────────────────────────── */

  function RosterPanel({ report, columns, comparisonMode }) {
    const [selectedMonthTab, setSelectedMonthTab] = useState('all');
    const badgeClasses = { Selected: 'bg-green-50 text-green-700', Rejected: 'bg-red-50 text-red-700', Dropped: 'bg-orange-50 text-orange-700', Undecided: 'bg-blue-50 text-blue-600' };
    const showAssessment = !!report.meta.hasAssessment;
    const showInterview = !!report.meta.hasInterview;

    // CTC is not collected on every screening — hide each column unless somebody has a value,
    // matching how the Excel roster drops all-empty columns.
    const showCurrentCtc = useMemo(
      () => (report.rawCandidates || []).some((c) => c.currentCtc),
      [report.rawCandidates]
    );
    const showExpectedCtc = useMemo(
      () => (report.rawCandidates || []).some((c) => c.expectedCtc),
      [report.rawCandidates]
    );
    // Region only exists for orgs the server sends `meta.regions` for (Burger King).
    const showRegion = useMemo(
      () => (report.rawCandidates || []).some((c) => c.region),
      [report.rawCandidates]
    );

    // Org application questions (Division / HQ / Drive Location for Ajanta MR), keeping only
    // those somebody actually answered so an unused question never adds a column of dashes.
    const customFieldDefs = useMemo(() => {
      return getRosterCustomFieldDefs(report.meta).filter((def) =>
        (report.rawCandidates || []).some((c) => c.customFields?.[def.fieldId])
      );
    }, [report.meta, report.rawCandidates]);

    const isMonthMode = comparisonMode === 'month';
    const monthCols = columns || [];

    const monthCounts = useMemo(() => {
      if (!isMonthMode || !monthCols.length) return {};
      const map = {};
      monthCols.forEach((m) => {
        map[m.id] = 0;
      });
      (report.rawCandidates || []).forEach((c) => {
        const dateVal = c.appliedDate || c.appliedAt;
        if (!dateVal) return;
        const mId = String(dateVal).slice(0, 7);
        if (map[mId] !== undefined) {
          map[mId] += 1;
        }
      });
      return map;
    }, [isMonthMode, monthCols, report.rawCandidates]);

    const filteredCandidates = useMemo(() => {
      if (!isMonthMode || selectedMonthTab === 'all') {
        return report.rawCandidates || [];
      }
      return (report.rawCandidates || []).filter((c) => {
        const dateVal = c.appliedDate || c.appliedAt;
        if (!dateVal) return false;
        return String(dateVal).slice(0, 7) === selectedMonthTab;
      });
    }, [isMonthMode, selectedMonthTab, report.rawCandidates]);

    return (
      <section>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">
            Raw data - Candidate roster
          </h2>
          <span className="text-xs text-slate-400">{filteredCandidates.length} candidates</span>
        </div>

        {/* Month Filter Tabs (Only in By Month mode) */}
        {isMonthMode && monthCols.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1.5 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xs">
            <button
              type="button"
              onClick={() => setSelectedMonthTab('all')}
              className={cn(
                'rounded-lg px-3 py-1.5 text-xs font-bold transition-all',
                selectedMonthTab === 'all'
                  ? 'bg-[#007BFF] text-white shadow-xs'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              )}
            >
              All ({report.summary.totalCandidates})
            </button>
            {monthCols.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setSelectedMonthTab(m.id)}
                className={cn(
                  'rounded-lg px-3 py-1.5 text-xs font-bold transition-all flex items-center gap-1.5',
                  selectedMonthTab === m.id
                    ? 'bg-[#007BFF] text-white shadow-xs'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                )}
              >
                <span>{m.title}</span>
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-[10px] font-black',
                    selectedMonthTab === m.id
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-100 text-slate-700'
                  )}
                >
                  {monthCounts[m.id] || 0}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white p-2 sm:p-4 shadow-sm">
          <div className="max-h-[600px] overflow-auto minimal-scrollbar relative">
            <table className="min-w-full text-left text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 shadow-[0_1px_0_0_#e2e8f0]">
                <tr className="border-b border-slate-200 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Candidate Name</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Hiring Position</th>
                  {showRegion && <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Region</th>}
                  {customFieldDefs.map((def) => (
                    <th key={def.fieldId} className="px-5 py-3 whitespace-nowrap bg-slate-50">{def.label}</th>
                  ))}
                  {showInterview && <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Interview Score</th>}
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Current Company</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Experience (Years)</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Education</th>
                  {showCurrentCtc && <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Current CTC</th>}
                  {showExpectedCtc && <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Expected CTC</th>}
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Location</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Hiring Decision</th>
                  <th className="px-5 py-3 whitespace-nowrap bg-slate-50">Applied Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredCandidates.map((candidate) => (
                  <tr
                    key={`${candidate.candidate}-${candidate.appliedAt}`}
                    className="border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50/70"
                  >
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-blue-50 text-xs font-black text-[#007BFF]">
                          {candidate.initials}
                        </span>
                        <span className="font-semibold text-slate-950">{candidate.candidate}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.jobTitle || '-'}</td>
                    {showRegion && (
                      <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.region || '-'}</td>
                    )}
                    {customFieldDefs.map((def) => (
                      <td key={def.fieldId} className="px-5 py-3 whitespace-nowrap text-slate-700">
                        {candidate.customFields?.[def.fieldId] || '-'}
                      </td>
                    ))}
                    {showInterview && (
                      <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.interviewScore ?? '-'}</td>
                    )}
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.currentCompany || '-'}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.experienceYears ?? '-'}</td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.education || '-'}</td>
                    {showCurrentCtc && (
                      <td className="px-5 py-3 whitespace-nowrap tabular-nums text-slate-700">{candidate.currentCtc || '-'}</td>
                    )}
                    {showExpectedCtc && (
                      <td className="px-5 py-3 whitespace-nowrap tabular-nums text-slate-700">{candidate.expectedCtc || '-'}</td>
                    )}
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.location || '-'}</td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span
                        className={cn(
                          'rounded-full px-3 py-1 text-xs font-bold',
                          badgeClasses[candidate.outcome] || badgeClasses.Undecided
                        )}
                      >
                        {candidate.outcome}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">{candidate.appliedAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  }

  /* ──────────────────────────────────────────────────────────────────
     EXCEL EXPORT HELPERS
     ────────────────────────────────────────────────────────────── */

  function applySheetDefaults(
    ws,
    { freezeHeader = true, widths = [], autoFilter = true, headerRow = 0 } = {}
  ) {
    ws['!cols'] = widths.map((wch) => ({ wch }));
    if (freezeHeader) ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1:A1');
    if (autoFilter) {
      const filterRange = { s: { r: headerRow, c: range.s.c }, e: { r: range.e.r, c: range.e.c } };
      ws['!autofilter'] = { ref: XLSX.utils.encode_range(filterRange) };
    }
    return ws;
  }

  function setNumberFormat(ws, addresses, format) {
    addresses.forEach((address) => { if (ws[address]) ws[address].z = format; });
  }

  function appendAoaSheet(wb, name, rows, widths, options = {}) {
    const ws = XLSX.utils.aoa_to_sheet(rows);
    applySheetDefaults(ws, { widths, ...options });
    XLSX.utils.book_append_sheet(wb, ws, name);
    return ws;
  }

  function appendJsonSheet(wb, name, rows, widths, options = {}) {
    const ws = XLSX.utils.json_to_sheet(rows);
    applySheetDefaults(ws, { widths, ...options });
    XLSX.utils.book_append_sheet(wb, ws, name);
    return ws;
  }

  function buildGroupedReportRowsAoa(candidates, labelName, getGroupKey) {
    const groups = new Map();
    (candidates || []).forEach((c) => {
      const key = String(getGroupKey(c) || '').trim() || 'Unassigned';
      if (!groups.has(key)) {
        groups.set(key, { name: key, total: 0, selected: 0, rejected: 0, dropped: 0, undecided: 0 });
      }
      const g = groups.get(key);
      g.total += 1;
      if (c.outcome === 'Selected') g.selected += 1;
      if (c.outcome === 'Dropped') g.dropped += 1;
      else if (c.outcome === 'Rejected') g.rejected += 1;
      else g.undecided += 1;
    });

    const header = [labelName, 'Total Candidates', 'Selected', 'Rejected', 'Dropped', 'Undecided'];
    const rows = Array.from(groups.values())
      .sort((a, b) => b.total - a.total)
      .map((g) => [g.name, g.total, g.selected, g.rejected, g.dropped, g.undecided]);

    return [header, ...rows];
  }

  function generateAsciiBar(pct, maxLength = 10) {
    const p = Math.max(0, Math.min(100, Number(pct) || 0));
    const filled = Math.round((p / 100) * maxLength);
    const empty = maxLength - filled;
    return '█'.repeat(filled) + '░'.repeat(empty) + ` ${p.toFixed(1)}%`;
  }

  /* ──────────────────────────────────────────────────────────────────
     EXPORT EXCEL
     ────────────────────────────────────────────────────────────── */

  function renderComparisonChartCanvas(title, categories, seriesData, width = 750, height = 400) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    ctx.fillStyle = '#1e293b';
    ctx.font = 'bold 18px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(title, width / 2, 34);

    const paddingLeft = 55;
    const paddingRight = 35;
    const paddingTop = 65;
    const paddingBottom = 75;
    const chartWidth = width - paddingLeft - paddingRight;
    const chartHeight = height - paddingTop - paddingBottom;

    let maxVal = 0;
    seriesData.forEach((s) => {
      (s.values || []).forEach((v) => {
        if (v > maxVal) maxVal = v;
      });
    });
    if (maxVal <= 0) maxVal = 10;
    const yMax = Math.ceil(maxVal * 1.15);

    const steps = 5;
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#f1f5f9';
    ctx.fillStyle = '#64748b';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'right';

    for (let i = 0; i <= steps; i++) {
      const val = Math.round((yMax / steps) * i);
      const y = paddingTop + chartHeight - (i / steps) * chartHeight;
      ctx.beginPath();
      ctx.moveTo(paddingLeft, y);
      ctx.lineTo(paddingLeft + chartWidth, y);
      ctx.stroke();
      ctx.fillText(String(val), paddingLeft - 8, y + 4);
    }

    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(paddingLeft, paddingTop + chartHeight);
    ctx.lineTo(paddingLeft + chartWidth, paddingTop + chartHeight);
    ctx.stroke();

    const numCats = categories.length;
    const catWidth = chartWidth / numCats;
    const numSeries = seriesData.length;
    const barGap = 6;
    const totalBarGroupWidth = Math.min(catWidth * 0.75, numSeries * 42);
    const barWidth = Math.max(14, (totalBarGroupWidth - (numSeries - 1) * barGap) / numSeries);

    const seriesColors = ['#3b82f6', '#f97316', '#10b981', '#8b5cf6', '#06b6d4'];

    categories.forEach((catLabel, catIdx) => {
      const groupCenterX = paddingLeft + catIdx * catWidth + catWidth / 2;
      const groupStartX = groupCenterX - ((numSeries * barWidth + (numSeries - 1) * barGap) / 2);

      seriesData.forEach((series, sIdx) => {
        const val = series.values[catIdx] || 0;
        const barH = (val / yMax) * chartHeight;
        const x = groupStartX + sIdx * (barWidth + barGap);
        const y = paddingTop + chartHeight - barH;

        ctx.fillStyle = series.color || seriesColors[sIdx % seriesColors.length];
        ctx.fillRect(x, y, barWidth, Math.max(barH, 2));

        ctx.fillStyle = '#0f172a';
        ctx.font = 'bold 11px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(String(val), x + barWidth / 2, Math.max(paddingTop + 14, y - 6));
      });

      ctx.fillStyle = '#334155';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(catLabel, groupCenterX, paddingTop + chartHeight + 22);
    });

    const legendY = height - 20;
    let legendX = width / 2 - (seriesData.length * 110) / 2;

    seriesData.forEach((series, sIdx) => {
      const color = series.color || seriesColors[sIdx % seriesColors.length];
      ctx.fillStyle = color;
      ctx.fillRect(legendX, legendY - 10, 14, 14);

      ctx.fillStyle = '#334155';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(series.name, legendX + 20, legendY);

      legendX += 110;
    });

    return canvas.toDataURL('image/png');
  }

  async function embedImageInXlsx(xlsxArrayBuffer, pngDataUrl, startCol = 0, startRow = 16) {
    if (!pngDataUrl) return xlsxArrayBuffer;
    try {
      const zip = await JSZip.loadAsync(xlsxArrayBuffer);

      // Decode PNG base64 to bytes
      const base64Data = pngDataUrl.replace(/^data:image\/png;base64,/, '');
      const binaryStr = window.atob(base64Data);
      const imageBytes = new Uint8Array(binaryStr.length);
      for (let i = 0; i < binaryStr.length; i++) {
        imageBytes[i] = binaryStr.charCodeAt(i);
      }
      zip.file('xl/media/image1.png', imageBytes);

      // Properly formed drawing XML with correct namespaces and EMU-based offsets
      // EMU = English Metric Units; 914400 EMU = 1 inch; colOff/rowOff must be in EMU
      const drawingXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<xdr:wsDr
  xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing"
  xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main"
  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"
  xmlns:xdr14="http://schemas.microsoft.com/office/drawing/2010/main">
  <xdr:oneCellAnchor>
    <xdr:from>
      <xdr:col>${startCol}</xdr:col>
      <xdr:colOff>0</xdr:colOff>
      <xdr:row>${startRow}</xdr:row>
      <xdr:rowOff>0</xdr:rowOff>
    </xdr:from>
    <xdr:ext cx="6858000" cy="3810000"/>
    <xdr:pic>
      <xdr:nvPicPr>
        <xdr:cNvPr id="2" name="Chart_Comparison" descr="Monthly Comparison Chart"/>
        <xdr:cNvPicPr preferRelativeResize="0">
          <a:picLocks noChangeAspect="1"/>
        </xdr:cNvPicPr>
      </xdr:nvPicPr>
      <xdr:blipFill>
        <a:blip r:embed="rId1" cstate="print">
          <a:extLst>
            <a:ext uri="{28A0092B-C50C-407E-A947-70E740481C1C}">
              <a14:useLocalDpi xmlns:a14="http://schemas.microsoft.com/office/drawing/2010/main" val="0"/>
            </a:ext>
          </a:extLst>
        </a:blip>
        <a:stretch>
          <a:fillRect/>
        </a:stretch>
      </xdr:blipFill>
      <xdr:spPr>
        <a:xfrm>
          <a:off x="0" y="0"/>
          <a:ext cx="6858000" cy="3810000"/>
        </a:xfrm>
        <a:prstGeom prst="rect">
          <a:avLst/>
        </a:prstGeom>
      </xdr:spPr>
    </xdr:pic>
    <xdr:clientData/>
  </xdr:oneCellAnchor>
</xdr:wsDr>`;
      zip.file('xl/drawings/drawing1.xml', drawingXml);

      // Drawing relationships file
      const drawingRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/image1.png"/>
</Relationships>`;
      zip.file('xl/drawings/_rels/drawing1.xml.rels', drawingRels);

      // Patch sheet1.xml to add drawing reference before </worksheet>
      // Must be after <pageSetup/> or similar; we add it just before </worksheet>
      let sheetXml = await zip.file('xl/worksheets/sheet1.xml').async('text');

      // Ensure the worksheet has the r namespace declared
      if (!sheetXml.includes('xmlns:r=')) {
        sheetXml = sheetXml.replace(
          '<worksheet ',
          '<worksheet xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" '
        );
      }
      // Add drawing element if not already present
      if (!sheetXml.includes('<drawing ')) {
        sheetXml = sheetXml.replace('</worksheet>', '<drawing r:id="rId2"/></worksheet>');
      }
      zip.file('xl/worksheets/sheet1.xml', sheetXml);

      // Patch or create _rels/sheet1.xml.rels with drawing relationship
      let sheetRels = '';
      const sheetRelsFile = zip.file('xl/worksheets/_rels/sheet1.xml.rels');
      if (sheetRelsFile) {
        sheetRels = await sheetRelsFile.async('text');
      } else {
        sheetRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>`;
      }
      if (!sheetRels.includes('drawings/drawing1.xml')) {
        sheetRels = sheetRels.replace(
          '</Relationships>',
          '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing1.xml"/></Relationships>'
        );
        zip.file('xl/worksheets/_rels/sheet1.xml.rels', sheetRels);
      }

      // Patch [Content_Types].xml
      let contentTypes = await zip.file('[Content_Types].xml').async('text');
      if (!contentTypes.includes('image/png')) {
        contentTypes = contentTypes.replace(
          '</Types>',
          '<Default Extension="png" ContentType="image/png"/></Types>'
        );
      }
      if (!contentTypes.includes('drawings/drawing1.xml')) {
        contentTypes = contentTypes.replace(
          '</Types>',
          '<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/></Types>'
        );
      }
      zip.file('[Content_Types].xml', contentTypes);

      return await zip.generateAsync({ type: 'arraybuffer', compression: 'DEFLATE', compressionOptions: { level: 6 } });
    } catch (err) {
      console.error('Failed to embed chart image into Excel:', err);
      return xlsxArrayBuffer;
    }
  }

  async function exportExcel(report, options = {}) {
    const {
      comparisonMode,
      monthColumns = [],
      perMonthData,
      columns = [],
      perColumnData,
      getColumnId,
      columnLabel = 'Column',
      regionNames = [],
      nestColumns = [],
      nestGetColumnId,
      nestLabel = 'Position',
    } = options;
    const isMonthMode = comparisonMode === 'month' && monthColumns.length > 0;
    const wb = XLSX.utils.book_new();
    wb.Props = {
      Title: `TalentCrux Report - ${report.meta.jobTitle || 'Hiring Analytics'}`,
      Subject: 'Hiring analytics report',
      Author: 'TalentCrux',
      CreatedDate: new Date(),
    };

    const applyTableStyles = (ws, rows, colCount, skipRows = 0) => {
      const titleStyle = {
        font: { bold: true, color: { rgb: "1E3A8A" } },
        fill: { patternType: "solid", fgColor: { rgb: "DBEAFE" } },
        alignment: { vertical: 'center', horizontal: 'left' }
      };
      const headerStyle = {
        font: { bold: true, color: { rgb: "000000" } },
        fill: { patternType: "solid", fgColor: { rgb: "E5E7EB" } },
        alignment: { vertical: 'center', horizontal: 'left' }
      };
      const dataStyle = {
        font: { color: { rgb: "000000" } }
      };
      const thinBorder = {
        top: { style: 'thin', color: { rgb: "000000" } },
        bottom: { style: 'thin', color: { rgb: "000000" } },
        left: { style: 'thin', color: { rgb: "000000" } },
        right: { style: 'thin', color: { rgb: "000000" } }
      };

      rows.forEach((row, rIdx) => {
        if (rIdx < skipRows) return;
        if (row.length === 0) return;

        const firstCell = row[0];
        // Section headers are emitted as a label with every other cell blank. Testing the row
        // shape rather than a string prefix keeps this working when the column axis (and so the
        // label — "Hiring Position: …", "Region: …", "Month: …") changes.
        const isTitle =
          typeof firstCell === 'string' &&
          firstCell.trim() !== '' &&
          row.slice(1).every((cell) => cell === '' || cell === null || cell === undefined);
        
        const isHeader = 
          (row.includes('Outcome') && (row.includes('Count') || row.includes('Total'))) || 
          (row.includes('Stage') && (row.includes('Conversion Rate') || row.includes('Overall Rate'))) || 
          row.includes('Total Applicants') ||
          row.includes('Report Mode') ||
          (row.includes('Month') && row.includes('Applied Candidates'));

        const colsToStyle = (row.length > 1 || isTitle) ? colCount : row.length;

        for (let c = 0; c < colsToStyle; c++) {
          let cell = ws[XLSX.utils.encode_cell({r: rIdx, c: c})];
          if (!cell) {
            cell = { t: 's', v: '' };
            ws[XLSX.utils.encode_cell({r: rIdx, c: c})] = cell;
          }
          cell.s = cell.s || {};
          cell.s.border = thinBorder;

          if (isTitle) {
            cell.s.fill = titleStyle.fill;
            cell.s.font = titleStyle.font;
            cell.s.alignment = titleStyle.alignment;
          } else if (isHeader) {
            cell.s.fill = headerStyle.fill;
            cell.s.font = headerStyle.font;
            cell.s.alignment = headerStyle.alignment;
          } else {
             cell.s.font = cell.s.font || dataStyle.font;
          }
        }
      });

      if (ws['!ref']) {
        const range = XLSX.utils.decode_range(ws['!ref']);
        range.e.c = Math.max(range.e.c, colCount - 1);
        ws['!ref'] = XLSX.utils.encode_range(range);
      }
    };

    // One stage list, outcome list and panel list for the whole workbook, so no two sheets
    // can disagree — and so `Dropped` is present here exactly when it is present on screen.
    const funnelStages = getFunnelStages(report);
    const outcomeKeys = getOutcomeKeys(report);
    const showDropped = outcomeKeys.includes('Dropped');

    const outcomeData = report.outcomeBreakdown.filter(
      (x) => x.name !== 'On hold' && (showDropped || x.name !== 'Dropped')
    );

    const categoryPanels = getCategoryPanels(report.meta);
    const funnelData = countFunnelStages(report, funnelStages).map((s) => ({ stage: s.label, count: s.count }));

    // Count from the id list, not by splitting the joined names — screening names contain commas.
    const screeningCount = (report.meta.screeningId || []).length;
    const screeningSummary = screeningCount > 3
      ? `${screeningCount} screenings`
      : report.meta.screeningName || 'All screenings';

    // 1. Overview Sheet
    const overviewRows = [
      ['TalentCrux Hiring Report'],
      ['Report Mode', `${columnLabel} Breakdown (By ${columnLabel})`],
      ['Regions Selected', regionNames.length ? regionNames.join(', ') : 'All regions'],
      ['Hiring Positions', columns.length ? columns.map((c) => c.title).join(', ') : '—'],
      ['Screenings', screeningSummary],
      ['Date Frame', `${formatExportDate(report.meta.from)} to ${formatExportDate(report.meta.to)}`],
      [],
    ];
    const OVERVIEW_META_ROWS = overviewRows.length;

    let overviewColWidths = [];

    if (isMonthMode) {
      overviewRows.push(
        ['Summary Outcome Breakdown'],
        ['Outcome', 'Count', 'Percentage', 'Visual Bar'],
        ...outcomeData.map((x) => [x.name, x.value, x.pct / 100, generateAsciiBar(x.pct)]),
        [],
        ['Summary Hiring Funnel'],
        ['Stage', 'Count', 'Conversion Rate'],
        ...funnelData.map((x) => [
          x.stage,
          x.count,
          report.summary.totalCandidates ? `${((x.count / report.summary.totalCandidates) * 100).toFixed(1)}%` : '0%',
        ])
      );
      overviewColWidths = [26, 18, 16, 22];
    } else {
      const colTitles = columns.map((c) => c.title || c.id);
      const colIds = columns.map((c) => c.id);

      overviewRows.push(['Summary Outcome Breakdown']);
      overviewRows.push(['Outcome', ...colTitles, 'Total', 'Share %', 'Visual Bar']);

      outcomeKeys.forEach((outcomeKey) => {
        const rowCounts = colIds.map((cId) => perColumnData?.outcomes?.[cId]?.[outcomeKey] || 0);
        const total = rowCounts.reduce((sum, n) => sum + n, 0);
        const pct = report.summary.totalCandidates ? (total / report.summary.totalCandidates) * 100 : 0;
        overviewRows.push([outcomeKey, ...rowCounts, total, `${pct.toFixed(1)}%`, generateAsciiBar(pct)]);
      });
      const columnTotalsRow = colIds.map((cId) => {
        const o = perColumnData?.outcomes?.[cId] || {};
        return (o.Selected || 0) + (o.Rejected || 0) + (o.Dropped || 0) + (o.Undecided || 0);
      });
      overviewRows.push(['Total', ...columnTotalsRow, report.summary.totalCandidates || 0, '100.0%', generateAsciiBar(100)]);

      overviewRows.push([]);
      overviewRows.push(['Summary Hiring Funnel']);
      overviewRows.push(['Stage', ...colTitles, 'Total', 'Overall Rate']);

      funnelStages.forEach((stg) => {
        const rowCounts = colIds.map((cId) => perColumnData?.funnel?.[cId]?.[stg.key] || 0);
        const total = rowCounts.reduce((sum, n) => sum + n, 0);
        const rate = report.summary.totalCandidates ? `${((total / report.summary.totalCandidates) * 100).toFixed(1)}%` : '0%';
        overviewRows.push([stg.label, ...rowCounts, total, rate]);
      });

      overviewColWidths = [26, ...colTitles.map(() => 12), 12, 14, 22];
    }

    const overviewWs = appendAoaSheet(wb, 'Overview', overviewRows, overviewColWidths, { autoFilter: false, freezeHeader: false });
    // Title row tall, then one entry per metadata row, then the blank separator.
    overviewWs['!rows'] = [{ hpt: 22 }, {}, {}, {}, {}, {}, { hpt: 18 }];

    if (isMonthMode) {
      // Outcome percentages sit in column C, starting one row after the metadata block + section title + header.
      const firstPctRow = OVERVIEW_META_ROWS + 3; // 1-indexed Excel row
      Object.keys(overviewWs).filter((key) => /^C\d+$/.test(key)).forEach((key) => {
        const rowNum = parseInt(key.substring(1), 10);
        if (rowNum >= firstPctRow && rowNum < firstPctRow + outcomeData.length) {
          overviewWs[key].z = '0.0%';
        }
      });
    }

    // Apply styles to Overview, skipping the metadata rows at the top
    applyTableStyles(overviewWs, overviewRows, overviewColWidths.length, OVERVIEW_META_ROWS);

    // Generate Chart Image
    let chartPngUrl = null;
    if (isMonthMode && perMonthData && monthColumns.length > 0) {
      const chartTitle = monthColumns.length >= 2
        ? `${monthColumns[0].title} to ${monthColumns[monthColumns.length - 1].title} Comparison`
        : `${monthColumns[0].title} Outcome Breakdown`;

      // The renderer reads `values` positionally against `categories`, so both are built from
      // the same key list — otherwise every bar after a skipped outcome is mislabelled.
      const chartCategories = [...outcomeKeys, 'Total'];
      const chartSeries = monthColumns.slice(0, 5).map((m) => {
        const o = perMonthData.outcomes[m.id] || {};
        const values = outcomeKeys.map((key) => o[key] || 0);
        const tot = (o.Selected || 0) + (o.Rejected || 0) + (o.Dropped || 0) + (o.Undecided || 0);
        return {
          name: m.title,
          values: [...values, tot],
        };
      });

      chartPngUrl = renderComparisonChartCanvas(chartTitle, chartCategories, chartSeries, 760, 380);
    }

    // 2. Month-Wise Breakdown Sheets (if comparisonMode === 'month')
    if (isMonthMode && perMonthData) {
      const mTitles = monthColumns.map((m) => m.title);
      const mIds = monthColumns.map((m) => m.id);

      // The months are the columns here, so each stacked sub-table is one hiring position.
      const nestSections = nestColumns;
      const sectionCandidates = (section) =>
        typeof nestGetColumnId === 'function'
          ? (report.rawCandidates || []).filter((c) => nestGetColumnId(c) === section.id)
          : [];

      // (A) Outcome by Month Sheet (stacked per hiring position)
      const outcomeMonthRows = [];
      outcomeMonthRows.push(['Total (All Selected Regions)', '', '', '']);
      outcomeMonthRows.push(['Outcome', ...mTitles, 'Total', 'Share %', 'Visual Bar']);
      
      outcomeKeys.forEach((outcomeKey) => {
        const rowCounts = mIds.map((mId) => perMonthData.outcomes[mId]?.[outcomeKey] || 0);
        const total = rowCounts.reduce((sum, n) => sum + n, 0);
        const pct = report.summary.totalCandidates ? (total / report.summary.totalCandidates) * 100 : 0;
        outcomeMonthRows.push([outcomeKey, ...rowCounts, total, `${pct.toFixed(1)}%`, generateAsciiBar(pct)]);
      });
      
      const monthTotalsRow = mIds.map((mId) => {
        const o = perMonthData.outcomes[mId] || {};
        return (o.Selected || 0) + (o.Rejected || 0) + (o.Dropped || 0) + (o.Undecided || 0);
      });
      outcomeMonthRows.push(['Total', ...monthTotalsRow, report.summary.totalCandidates || 0, '100.0%', generateAsciiBar(100)]);
      outcomeMonthRows.push([]); // separator

      nestSections.forEach((section) => {
        const rows = sectionCandidates(section);
        const sectionMonthData = computePerMonthBreakdowns(rows, monthColumns, categoryPanels) || { outcomes: {}, funnel: {} };
        const sectionTotal = rows.length;

        outcomeMonthRows.push([`${nestLabel}: ${section.title || section.id}`, '', '', '']);
        outcomeMonthRows.push(['Outcome', ...mTitles, 'Total', 'Share %', 'Visual Bar']);
        
        outcomeKeys.forEach((outcomeKey) => {
          const rowCounts = mIds.map((mId) => sectionMonthData.outcomes[mId]?.[outcomeKey] || 0);
          const total = rowCounts.reduce((sum, n) => sum + n, 0);
          const pct = sectionTotal ? (total / sectionTotal) * 100 : 0;
          outcomeMonthRows.push([outcomeKey, ...rowCounts, total, `${pct.toFixed(1)}%`, generateAsciiBar(pct)]);
        });
        
        const sectionMonthTotalsRow = mIds.map((mId) => {
          const o = sectionMonthData.outcomes[mId] || {};
          return (o.Selected || 0) + (o.Rejected || 0) + (o.Dropped || 0) + (o.Undecided || 0);
        });
        outcomeMonthRows.push(['Total', ...sectionMonthTotalsRow, sectionTotal, '100.0%', generateAsciiBar(100)]);
        outcomeMonthRows.push([]); // separator
      });
      const outcomeMonthWs = appendAoaSheet(wb, 'Outcome by Month', outcomeMonthRows, [16, ...mTitles.map(() => 12), 12, 14, 22], { autoFilter: false });
      applyTableStyles(outcomeMonthWs, outcomeMonthRows, mTitles.length + 4);

      // (B) Hiring Funnel by Month Sheet (stacked per hiring position)
      const funnelMonthRows = [];
      funnelMonthRows.push(['Total (All Selected Regions)', '', '', '']);
      funnelMonthRows.push(['Stage', ...mTitles, 'Total', 'Overall Rate']);

      funnelStages.forEach((stg) => {
        const rowCounts = mIds.map((mId) => perMonthData.funnel[mId]?.[stg.key] || 0);
        const total = rowCounts.reduce((sum, n) => sum + n, 0);
        const rate = report.summary.totalCandidates ? `${((total / report.summary.totalCandidates) * 100).toFixed(1)}%` : '0%';
        funnelMonthRows.push([stg.label, ...rowCounts, total, rate]);
      });
      funnelMonthRows.push([]); // separator

      nestSections.forEach((section) => {
        const rows = sectionCandidates(section);
        const sectionMonthData = computePerMonthBreakdowns(rows, monthColumns, categoryPanels) || { outcomes: {}, funnel: {} };
        const sectionTotal = rows.length;

        funnelMonthRows.push([`${nestLabel}: ${section.title || section.id}`, '', '', '']);
        funnelMonthRows.push(['Stage', ...mTitles, 'Total', 'Overall Rate']);
        
        funnelStages.forEach((stg) => {
          const rowCounts = mIds.map((mId) => sectionMonthData.funnel[mId]?.[stg.key] || 0);
          const total = rowCounts.reduce((sum, n) => sum + n, 0);
          const rate = sectionTotal ? `${((total / sectionTotal) * 100).toFixed(1)}%` : '0%';
          funnelMonthRows.push([stg.label, ...rowCounts, total, rate]);
        });
        funnelMonthRows.push([]); // separator
      });
      const funnelMonthWs = appendAoaSheet(wb, 'Hiring Funnel by Month', funnelMonthRows, [20, ...mTitles.map(() => 12), 12, 16], { autoFilter: false });
      applyTableStyles(funnelMonthWs, funnelMonthRows, mTitles.length + 3);

      // (C) One sheet per MR category, broken down by month
      categoryPanels.forEach((panel) => {
        const catData = capCategoryData(perMonthData.categories?.[panel.key], panel.topN, mIds);
        if (!catData?.length) return;
        const rows = [[panel.header, ...mTitles, 'Total Applicants', 'Share %', 'Visual Distribution']];
        catData.forEach((item) => {
          const mCounts = mIds.map((mId) => item.byColumn?.[mId] || 0);
          const pct = report.summary.totalCandidates ? (item.count / report.summary.totalCandidates) * 100 : 0;
          rows.push([item.name || 'Unassigned', ...mCounts, item.count, `${pct.toFixed(1)}%`, generateAsciiBar(pct)]);
        });
        const ws = appendAoaSheet(wb, panel.sheet, rows, [26, ...mTitles.map(() => 12), 16, 14, 22], { autoFilter: false });
        applyTableStyles(ws, rows, mTitles.length + 4);
      });

      // (D) Monthly Applications Trend Sheet (stacked per hiring position)
      // Middle stages vary by screening setup, so the columns follow the funnel stage list.
      const midStages = funnelStages.filter((s) => s.key !== 'applied' && s.key !== 'selected');
      const trendHeader = ['Month', 'Applied Candidates', ...midStages.map((s) => s.label), 'Selected', 'Selection Rate %', 'Trend Bar Visual'];
      const trendColCount = trendHeader.length;
      const timelineRows = [];
      timelineRows.push(['Total (All Selected Regions)', '', '', '']);
      timelineRows.push(trendHeader);

      const pushTrendRows = (data) => {
        monthColumns.forEach((m) => {
          const app = data.funnel[m.id]?.applied || 0;
          const mids = midStages.map((s) => data.funnel[m.id]?.[s.key] || 0);
          const sel = data.outcomes[m.id]?.Selected || 0;
          const rate = app > 0 ? (sel / app) * 100 : 0;
          timelineRows.push([m.title, app, ...mids, sel, `${rate.toFixed(1)}%`, generateAsciiBar(rate)]);
        });
      };

      pushTrendRows(perMonthData);
      timelineRows.push([]); // separator

      nestSections.forEach((section) => {
        const rows = sectionCandidates(section);
        const sectionMonthData = computePerMonthBreakdowns(rows, monthColumns, categoryPanels) || { outcomes: {}, funnel: {} };

        timelineRows.push([`${nestLabel}: ${section.title || section.id}`, '', '', '']);
        timelineRows.push(trendHeader);
        pushTrendRows(sectionMonthData);
        timelineRows.push([]); // separator
      });
      const timelineWs = appendAoaSheet(wb, 'Monthly Applications Trend', timelineRows, [16, 20, ...midStages.map(() => 18), 14, 16, 24], { autoFilter: false });
      applyTableStyles(timelineWs, timelineRows, trendColCount);
    } else {
      // Breakdown sheets, one stacked sub-table per column on the active axis.
      const colTitles = columns.map((c) => c.title || c.id);
      const colIds = columns.map((c) => c.id);

      // (A) Per-column outcome summary stacked tables
      const outcomeStackedRows = [];
      outcomeStackedRows.push(['Total (All Selected Regions)', '', '', '']);
      outcomeStackedRows.push(['Outcome', 'Count', 'Percentage', 'Visual Bar']);
      outcomeData.forEach(x => {
         outcomeStackedRows.push([x.name, x.value, `${(x.pct).toFixed(1)}%`, generateAsciiBar(x.pct)]);
      });
      outcomeStackedRows.push(['Total', report.summary.totalCandidates || 0, '100.0%', generateAsciiBar(100)]);
      outcomeStackedRows.push([]); // Empty row separator

      columns.forEach((column) => {
        outcomeStackedRows.push([`${columnLabel}: ${column.title || column.id}`, '', '', '']);
        outcomeStackedRows.push(['Outcome', 'Count', 'Percentage', 'Visual Bar']);
        
        const o = perColumnData?.outcomes?.[column.id] || {};
        // Total counts every outcome, including dropped, so the rows always add up to it.
        const total = (o.Selected || 0) + (o.Rejected || 0) + (o.Dropped || 0) + (o.Undecided || 0);

        const makeRow = (name, val) => {
           const pct = total ? (val / total) * 100 : 0;
           return [name, val, `${pct.toFixed(1)}%`, generateAsciiBar(pct)];
        };

        outcomeKeys.forEach((key) => outcomeStackedRows.push(makeRow(key, o[key] || 0)));
        outcomeStackedRows.push(['Total', total, '100.0%', generateAsciiBar(100)]);
        outcomeStackedRows.push([]); // Empty row separator
      });
      const outcomeWs = appendAoaSheet(wb, 'Outcome Summary', outcomeStackedRows, [22, 14, 16, 22], { autoFilter: false });
      applyTableStyles(outcomeWs, outcomeStackedRows, 4);

      // (B) Per-column hiring funnel stacked tables
      const funnelStackedRows = [];
      funnelStackedRows.push(['Total (All Selected Regions)', '', '']);
      funnelStackedRows.push(['Stage', 'Count', 'Conversion Rate']);
      funnelData.forEach(x => {
         const rate = report.summary.totalCandidates ? (x.count / report.summary.totalCandidates) * 100 : 0;
         funnelStackedRows.push([x.stage, x.count, `${rate.toFixed(1)}%`]);
      });
      funnelStackedRows.push([]); // Empty row separator

      columns.forEach((column) => {
        funnelStackedRows.push([`${columnLabel}: ${column.title || column.id}`, '', '']);
        funnelStackedRows.push(['Stage', 'Count', 'Conversion Rate']);
        
        const f = perColumnData?.funnel?.[column.id] || {};
        const app = f.applied || 0;

        const makeRow = (name, val) => {
           const rate = app ? (val / app) * 100 : 0;
           return [name, val, `${rate.toFixed(1)}%`];
        };

        funnelStages.forEach((stg) => {
          funnelStackedRows.push(makeRow(stg.label, f[stg.key] || 0));
        });
        funnelStackedRows.push([]);
      });
      const funnelWs = appendAoaSheet(wb, 'Hiring Funnel', funnelStackedRows, [24, 14, 16], { autoFilter: false });
      applyTableStyles(funnelWs, funnelStackedRows, 3);

      // (C) One sheet per category panel, with a column per axis entry
      categoryPanels.forEach((panel) => {
        const catData = capCategoryData(perColumnData?.categories?.[panel.key], panel.topN, colIds);
        if (!catData?.length) return;
        const rows = [[panel.header, ...colTitles, 'Total Applicants', 'Share %', 'Visual Distribution']];
        catData.forEach((item) => {
          const cCounts = colIds.map((cId) => item.byColumn?.[cId] || 0);
          const pct = report.summary.totalCandidates ? (item.count / report.summary.totalCandidates) * 100 : 0;
          rows.push([item.name || 'Unassigned', ...cCounts, item.count, `${pct.toFixed(1)}%`, generateAsciiBar(pct)]);
        });
        const ws = appendAoaSheet(wb, panel.sheet, rows, [26, ...colTitles.map(() => 12), 16, 14, 22]);
        applyTableStyles(ws, rows, colTitles.length + 4);
      });

      // (D) Applications Over Time
      const appTimeWs = appendJsonSheet(
        wb,
        'Applications Over Time',
        report.applicationsOverTime.map((x) => ({
          Date: x.date || x.label,
          Label: x.label,
          Applications: x.count,
          'Visual Bar': generateAsciiBar(report.summary.totalCandidates ? (x.count / report.summary.totalCandidates) * 100 : 0),
        })),
        [16, 16, 16, 22]
      );
      if (appTimeWs['!ref']) {
        const range = XLSX.utils.decode_range(appTimeWs['!ref']);
        const thinBorder = {
          top: { style: 'thin', color: { rgb: "000000" } },
          bottom: { style: 'thin', color: { rgb: "000000" } },
          left: { style: 'thin', color: { rgb: "000000" } },
          right: { style: 'thin', color: { rgb: "000000" } }
        };
        const headerStyle = {
          font: { bold: true, color: { rgb: "000000" } },
          fill: { patternType: "solid", fgColor: { rgb: "E5E7EB" } },
          alignment: { vertical: 'center', horizontal: 'left' }
        };
        for (let r = range.s.r; r <= range.e.r; r++) {
          for (let c = range.s.c; c <= range.e.c; c++) {
            const cellAddr = XLSX.utils.encode_cell({r, c});
            let cell = appTimeWs[cellAddr];
            if (!cell) {
               cell = { t: 's', v: '' };
               appTimeWs[cellAddr] = cell;
            }
            cell.s = cell.s || {};
            cell.s.border = thinBorder;
            if (r === 0) {
              cell.s.fill = headerStyle.fill;
              cell.s.font = headerStyle.font;
              cell.s.alignment = headerStyle.alignment;
            }
          }
        }
      }
    }

    // Columns that are empty for every candidate (e.g. Interview Score on an assessment-only
    // screening, Expected CTC where it is never collected) are dropped rather than exported blank.
    const candidates = report.rawCandidates || [];
    const rosterColumns = [
      { header: 'Candidate Name', width: 24, value: (c) => c.candidate },
      { header: 'Email Address', width: 26, value: (c) => c.email || '' },
      { header: 'Phone Number', width: 16, value: (c) => c.phone || '' },
      { header: 'Hiring Position', width: 24, value: (c) => c.jobTitle || '' },
      // Burger King only; the all-empty filter below drops it for every other org.
      { header: 'Region', width: 14, value: (c) => c.region || '' },
      // Org application questions, in the order they appear on the application form.
      ...getRosterCustomFieldDefs(report.meta).map((def) => ({
        header: def.label,
        width: Math.min(30, Math.max(16, def.label.length + 4)),
        value: (c) => c.customFields?.[def.fieldId] || '',
      })),
      { header: 'Screening', width: 22, value: (c) => c.screeningName || '' },
      { header: 'Location', width: 18, value: (c) => c.location || '' },
      { header: 'Current Company', width: 24, value: (c) => c.currentCompany || '' },
      { header: 'Experience (Years)', width: 18, value: (c) => c.experienceYears ?? '' },
      { header: 'Education', width: 20, value: (c) => c.education || '' },
      { header: 'Current CTC', width: 16, value: (c) => c.currentCtc || '' },
      { header: 'Expected CTC', width: 16, value: (c) => c.expectedCtc || '' },
      { header: 'Resume Score', width: 14, value: (c) => c.resumeScore ?? '' },
      { header: 'Assessment Score', width: 18, value: (c) => c.assessmentScore ?? '' },
      { header: 'Interview Score', width: 16, value: (c) => c.interviewScore ?? '' },
      { header: 'Hiring Decision', width: 16, value: (c) => c.outcome, always: true },
      { header: 'Rejection Reason', width: 24, value: (c) => c.notes ? parseBkRejectionNote(c.notes).reason : '' },
      { header: 'Rejection Remarks', width: 32, value: (c) => c.notes ? parseBkRejectionNote(c.notes).remarks : '' },
      { header: 'Charged', width: 12, value: (c) => c.isCharged ? 'Yes' : 'No' },
      { header: 'Applied Date', width: 14, value: (c) => c.appliedDate || c.appliedAt, always: true },
    ].filter(
      (col) => col.always || candidates.some((c) => {
        const v = col.value(c);
        return v !== '' && v !== null && v !== undefined;
      })
    );

    const rawRows = candidates.map((c) => {
      const row = {};
      rosterColumns.forEach((col) => { row[col.header] = col.value(c); });
      return row;
    });
    const rawWs = appendJsonSheet(wb, 'Raw Data - Roster', rawRows, rosterColumns.map((col) => col.width));
    if (rawWs['!ref']) {
      const range = XLSX.utils.decode_range(rawWs['!ref']);
      const thinBorder = {
        top: { style: 'thin', color: { rgb: "000000" } },
        bottom: { style: 'thin', color: { rgb: "000000" } },
        left: { style: 'thin', color: { rgb: "000000" } },
        right: { style: 'thin', color: { rgb: "000000" } }
      };
      const headerStyle = {
        font: { bold: true, color: { rgb: "000000" } },
        fill: { patternType: "solid", fgColor: { rgb: "E5E7EB" } },
        alignment: { vertical: 'center', horizontal: 'left' }
      };
      for (let r = range.s.r; r <= range.e.r; r++) {
        for (let c = range.s.c; c <= range.e.c; c++) {
          const cellAddr = XLSX.utils.encode_cell({r, c});
          let cell = rawWs[cellAddr];
          if (!cell) {
             cell = { t: 's', v: '' };
             rawWs[cellAddr] = cell;
          }
          cell.s = cell.s || {};
          cell.s.border = thinBorder;
          if (r === 0) {
            cell.s.fill = headerStyle.fill;
            cell.s.font = headerStyle.font;
            cell.s.alignment = headerStyle.alignment;
          }
        }
      }
    }

    const rawBuffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    let finalBuffer = rawBuffer;

    if (chartPngUrl) {
      // Place chart to the right of the Overview table with 1 column padding
      const chartStartCol = overviewColWidths.length + 1;
      const chartStartRow = 8;
      finalBuffer = await embedImageInXlsx(rawBuffer, chartPngUrl, chartStartCol, chartStartRow);
    }

    const blob = new Blob([finalBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const filePrefix = isMonthMode
      ? `TalentCrux_Monthly_Report_${compactDate(report.meta.from)}-to-${compactDate(report.meta.to)}`
      : getReportFileBaseName(report);

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filePrefix}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }

  /* ──────────────────────────────────────────────────────────────────
     PDF EXPORT HELPERS
     ────────────────────────────────────────────────────────────── */

  function pdfColor(hex) {
    const clean = String(hex || '#0f172a').replace('#', '');
    const value =
      clean.length === 3
        ? clean
          .split('')
          .map((x) => x + x)
          .join('')
        : clean;
    return [
      parseInt(value.slice(0, 2), 16),
      parseInt(value.slice(2, 4), 16),
      parseInt(value.slice(4, 6), 16),
    ];
  }

  function drawPdfKpi(doc, x, y, w, h, title, value, detail, color = '#007BFF') {
    const [r, g, b] = pdfColor(color);
    doc.setDrawColor(219, 228, 239);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(x, y, w, h, 8, 8, 'FD');
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(7.5);
    doc.setFont(undefined, 'bold');
    doc.text(String(title).toUpperCase(), x + 12, y + 18);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(20);
    doc.text(String(value), x + 12, y + 42);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(String(detail || ''), x + 12, y + 59, { maxWidth: w - 24 });
    doc.setFillColor(236, 242, 248);
    doc.roundedRect(x + 12, y + h - 13, w - 24, 2, 1, 1, 'F');
    doc.setFillColor(r, g, b);
    doc.roundedRect(x + 12, y + h - 13, (w - 24) * 0.72, 2, 1, 1, 'F');
  }

  function drawPdfBarList(doc, title, rows, x, y, w, options = {}) {
    const visibleRows = rows.slice(0, options.maxRows || rows.length);
    const hiddenRows = rows.slice(visibleRows.length);
    const displayRows = hiddenRows.length > 0 ? [...visibleRows, { name: `Others (${hiddenRows.length})`, count: hiddenRows.reduce((s, r) => s + (r.count ?? r.value ?? 0), 0), muted: true }] : visibleRows;
    const max = Math.max(...displayRows.map((r) => r.count || r.value || 0), 1);
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(title, x, y);
    let cy = y + 22;
    displayRows.forEach((row) => {
      const label = row.name || row.band || row.stage || row.label;
      const count = row.count ?? row.value;
      const barW = Math.max(8, ((count || 0) / max) * (w - 118));
      doc.setFontSize(8.5);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(51, 65, 85);
      doc.text(String(label), x, cy + 7, { maxWidth: 85 });
      doc.setFillColor(236, 242, 248);
      doc.roundedRect(x + 96, cy, w - 118, 8, 4, 4, 'F');
      doc.setFillColor(...pdfColor(row.muted ? '#94a3b8' : options.color || '#2f9bff'));
      doc.roundedRect(x + 96, cy, barW, 8, 4, 4, 'F');
      doc.setTextColor(15, 23, 42);
      doc.setFont(undefined, 'bold');
      doc.text(String(count), x + w - 8, cy + 7, { align: 'right' });
      cy += options.rowGap || 18;
    });
  }

  /* ──────────────────────────────────────────────────────────────────
     EXPORT PDF
     ────────────────────────────────────────────────────────────── */

  function exportPdf(report) {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    doc.setFillColor(247, 249, 252);
    doc.rect(0, 0, 842, 595, 'F');
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(28, 24, 786, 78, 10, 10, 'F');
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Reports', 44, 54);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text('Hiring analytics for a selected job and time frame - live, always current.', 44, 72);
    doc.text(`${report.meta.jobTitle} | ${report.meta.screeningName}`, 44, 88);
    doc.setTextColor(0, 123, 255);
    doc.setFont(undefined, 'bold');
    doc.text(`${formatExportDate(report.meta.from)} - ${formatExportDate(report.meta.to)}`, 790, 54, { align: 'right' });

    const kpiW = 146; const gap = 10;
    drawPdfKpi(doc, 32, 122, kpiW, 82, 'Total Candidates', report.summary.totalCandidates, `vs ${report.comparison.previousTotalCandidates} previous`);
    drawPdfKpi(doc, 32 + (kpiW + gap), 122, kpiW, 82, 'Selected', report.summary.selected, `vs ${report.comparison.previousSelected} previous`, '#16a34a');
    drawPdfKpi(doc, 32 + (kpiW + gap) * 2, 122, kpiW, 82, 'Selection Rate', `${report.summary.selectionRate}%`, `vs ${report.comparison.previousSelectionRate}%`);
    drawPdfKpi(doc, 32 + (kpiW + gap) * 3, 122, kpiW, 82, 'Avg Resume Score', report.summary.avgResumeScore, `vs ${report.comparison.previousAvgResumeScore}`);
    drawPdfKpi(doc, 32 + (kpiW + gap) * 4, 122, kpiW, 82, 'Rejected', report.summary.rejected, `vs ${report.comparison.previousRejected || 0} previous`, '#dc2626');

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(32, 224, 372, 142, 10, 10, 'F');
    doc.roundedRect(424, 224, 386, 142, 10, 10, 'F');
    drawPdfBarList(doc, 'Outcome breakdown', report.outcomeBreakdown, 52, 248, 322, { color: '#16a34a', maxRows: 4 });
    drawPdfBarList(doc, 'Resume score distribution', report.scoreDistribution, 444, 248, 336, { color: '#1683f8' });

    doc.setFillColor(255, 255, 255);
    doc.roundedRect(32, 386, 372, 140, 10, 10, 'F');
    doc.roundedRect(424, 386, 386, 140, 10, 10, 'F');
    drawPdfBarList(doc, 'Hiring funnel', report.funnel, 52, 410, 322, { color: '#007BFF', rowGap: 17 });
    drawPdfBarList(doc, 'Candidates by location', report.locationBreakdown, 444, 410, 336, { color: '#2f9bff', maxRows: 4, rowGap: 17 });

    doc.addPage('a4', 'landscape');
    doc.setFillColor(247, 249, 252);
    doc.rect(0, 0, 842, 595, 'F');
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Raw Data - Candidate Roster', 40, 44);
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Showing ${report.rawCandidates.length} candidate rows exported from this report.`, 40, 62);

    autoTable(doc, {
      startY: 86,
      head: [
        [
          'Candidate',
          'Location',
          'Resume',
          'Assessment',
          'Interview',
          'Current Company',
          'Expected CTC',
          'Applied',
          'Outcome',
        ],
      ],
      body: report.rawCandidates.map((c) => [
        c.candidate,
        c.location,
        c.resumeScore ?? '-',
        c.assessmentScore ?? '-',
        c.interviewScore ?? '-',
        c.currentCompany || '-',
        c.expectedCtc,
        c.appliedAt,
        c.outcome,
      ]),
      styles: {
        fontSize: 8,
        cellPadding: 5,
        textColor: [51, 65, 85],
        lineColor: [226, 232, 240],
        lineWidth: 0.5,
      },
      headStyles: { fillColor: [0, 123, 255] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 40, right: 40 },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i += 1) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(148, 163, 184);
      doc.text('TalentCrux Reports', 40, 570);
      doc.text(`Page ${i} of ${pageCount}`, 802, 570, { align: 'right' });
    }

    doc.save(`${getReportFileBaseName(report)}.pdf`);
  }

  /* ──────────────────────────────────────────────────────────────────
     MAIN PAGE COMPONENT
     ────────────────────────────────────────────────────────────── */

  const customScrollbarCss = `
  .minimal-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  .minimal-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .minimal-scrollbar::-webkit-scrollbar-thumb {
    background: #e2e8f0; 
    border-radius: 6px;
  }
  .minimal-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #cbd5e1; 
  }
  /* Label column stays put while a comparison table scrolls sideways on a narrow screen.
     Sticky cells need an opaque background: tinted rows get the solid equivalent of their tint. */
  @media (max-width: 1023px) {
    .report-sticky-first th:first-child,
    .report-sticky-first td:first-child {
      position: sticky;
      left: 0;
      z-index: 1;
      background-color: #ffffff;
      box-shadow: 1px 0 0 0 #e2e8f0;
    }
    .report-sticky-first tr[class~='bg-green-50/40'] td:first-child { background-color: #f9fefb; }
    .report-sticky-first tr[class~='bg-teal-50/40'] td:first-child { background-color: #f9fefd; }
    .report-sticky-first tr[class~='bg-slate-50/60'] td:first-child { background-color: #fbfcfd; }
  }
`;

  /* ──────────────────────────────────────────────────────────────────
     VISUAL CALENDAR POPOVER
     ────────────────────────────────────────────────────────────── */

  function VisualCalendarPopover({ initialRange, onApply, onClose }) {
    const [viewDate, setViewDate] = useState(() => {
      const d = initialRange?.from ? new Date(initialRange.from) : new Date();
      return Number.isNaN(d.getTime()) ? new Date() : d;
    });

    const [startDate, setStartDate] = useState(() => initialRange?.from || '');
    const [endDate, setEndDate] = useState(() => initialRange?.to || '');
    const [hoverDate, setHoverDate] = useState('');

    const currentYear = viewDate.getFullYear();
    const currentMonth = viewDate.getMonth();

    const monthName = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const prevMonth = () => {
      setViewDate(new Date(currentYear, currentMonth - 1, 1));
    };

    const nextMonth = () => {
      setViewDate(new Date(currentYear, currentMonth + 1, 1));
    };

    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayOffset = new Date(currentYear, currentMonth, 1).getDay();

    const handleDayClick = (dayStr) => {
      if (!startDate || (startDate && endDate)) {
        setStartDate(dayStr);
        setEndDate('');
      } else if (startDate && !endDate) {
        if (dayStr < startDate) {
          setEndDate(startDate);
          setStartDate(dayStr);
        } else {
          setEndDate(dayStr);
        }
      }
    };

    const effectiveEnd = endDate || (hoverDate && hoverDate > startDate ? hoverDate : '');

    const dayCells = [];
    for (let i = 0; i < firstDayOffset; i++) {
      dayCells.push(<div key={`empty-${i}`} className="h-8 w-8" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const mm = String(currentMonth + 1).padStart(2, '0');
      const dd = String(day).padStart(2, '0');
      const dayStr = `${currentYear}-${mm}-${dd}`;

      const isStart = dayStr === startDate;
      const isEnd = dayStr === endDate;
      const isSelectedBound = isStart || isEnd;
      const isInRange =
        startDate &&
        effectiveEnd &&
        dayStr >= startDate &&
        dayStr <= effectiveEnd;

      dayCells.push(
        <button
          key={dayStr}
          type="button"
          onClick={() => handleDayClick(dayStr)}
          onMouseEnter={() => {
            if (startDate && !endDate) setHoverDate(dayStr);
          }}
          className={cn(
            'relative flex h-8 w-8 items-center justify-center text-xs transition-all',
            isSelectedBound
              ? 'z-10 rounded-full bg-[#007BFF] text-white font-black shadow-sm'
              : isInRange
                ? 'bg-blue-100/90 text-blue-950 font-bold first:rounded-l-full last:rounded-r-full'
                : 'text-slate-700 hover:bg-slate-100 rounded-full font-medium'
          )}
        >
          {day}
        </button>
      );
    }

    const applyPreset = (days) => {
      const now = new Date();
      if (typeof days === 'number') {
        const past = new Date();
        past.setDate(now.getDate() - (days - 1));
        setStartDate(toIsoDate(past));
        setEndDate(toIsoDate(now));
        setViewDate(now);
      } else if (days === 'month') {
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        setStartDate(toIsoDate(first));
        setEndDate(toIsoDate(now));
        setViewDate(now);
      }
    };

    const totalDays =
      startDate && endDate
        ? Math.max(
          1,
          Math.round(
            (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)
          ) + 1
        )
        : 0;

    return (
      <div className="absolute left-0 top-full mt-2 z-50 w-80 rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl animate-in fade-in zoom-in-95 duration-150">
        {/* Quick Presets */}
        <div className="flex flex-wrap gap-1.5 border-b border-slate-100 pb-3 mb-3">
          {[
            ['7D', 7],
            ['14D', 14],
            ['30D', 30],
            ['90D', 90],
            ['This Month', 'month'],
          ].map(([label, val]) => (
            <button
              key={label}
              type="button"
              onClick={() => applyPreset(val)}
              className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-[#007BFF] transition-all"
            >
              {label}
            </button>
          ))}
        </div>

        {/* Month Header & Controls */}
        <div className="flex items-center justify-between mb-3 px-1">
          <strong className="text-sm font-bold text-slate-900">{monthName}</strong>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={prevMonth}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={nextMonth}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Day Name Headers */}
        <div className="grid grid-cols-7 text-center text-[11px] font-bold uppercase text-slate-400 mb-1">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
            <div key={i} className="h-6 flex items-center justify-center">
              {d}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-y-1 justify-items-center mb-3">
          {dayCells}
        </div>

        {/* Selected Summary */}
        {startDate && (
          <div className="rounded-xl bg-blue-50 p-2 text-xs text-blue-900 flex items-center justify-between mb-3 border border-blue-100">
            <span className="font-semibold truncate">
              {formatDateLabel(startDate)} {endDate ? `– ${formatDateLabel(endDate)}` : ''}
            </span>
            {totalDays > 0 && (
              <span className="shrink-0 rounded-md bg-blue-200/80 px-2 py-0.5 font-bold text-blue-900 text-[10px]">
                {totalDays} Days
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (startDate) {
                onApply({ from: startDate, to: endDate || startDate });
              }
            }}
            disabled={!startDate}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#007BFF] px-4 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-slate-300 transition-colors"
          >
            <Check className="h-3.5 w-3.5" />
            Apply Range
          </button>
        </div>
      </div>
    );
  }

export default function ReportsPageBK() {
    const [timeFrame, setTimeFrame] = useState('1m');
    const [range, setRange] = useState(() => getDefaultRange('1m'));
    // 'role' → one column per hiring position (the nine regional copies summed together),
    // 'region' → one column per selected region, 'month' → one column per month in range.
    const [comparisonMode, setComparisonMode] = useState('role');
    const [activeModal, setActiveModal] = useState(null);
    const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
    const [tempCustomRange, setTempCustomRange] = useState({ from: '', to: '' });
    // BK picks regions, not job posts — the jobs are derived from the pick below.
    const [selectedRegionIds, setSelectedRegionIds] = useState([]);
    const [selectedScreeningIds, setSelectedScreeningIds] = useState([]);

    const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
    const [isActionsOpen, setIsActionsOpen] = useState(false);
    const filterBarRef = useRef(null);
    const actionsMenuRef = useRef(null);

    useEffect(() => {
      function handleClickOutside(e) {
        if (actionsMenuRef.current && !actionsMenuRef.current.contains(e.target)) {
          setIsActionsOpen(false);
        }
      }
      if (isActionsOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isActionsOpen]);

    const closeDropdowns = useCallback(() => { setRegionDropdownOpen(false); }, []);

    useEffect(() => {
      function handleClick(e) { if (filterBarRef.current && !filterBarRef.current.contains(e.target)) closeDropdowns(); }
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }, [closeDropdowns]);

    const { data: jobs = [], isLoading: jobsLoading } = useJobs(true, true);

    // Regions are Teams, and which ones this user may report on is the server's call: every team
    // for the org admin, only their own teams for anyone else. A region with no open requisition
    // is still offered, so an empty region reads as empty instead of being invisible.
    const scopeQuery = useQuery({
      queryKey: ['reports', 'bk', 'scope'],
      queryFn: () => reportsApi.getBkScope(),
      staleTime: 5 * 60 * 1000,
      retry: false,
    });
    const isOrgScope = scopeQuery.data?.scope === 'org';
    const isRegionalScope = scopeQuery.data?.scope === 'teams';
    const scopeErrorMessage = scopeQuery.isError
      ? getApiErrorMessage(scopeQuery.error, 'Your regions could not be loaded.')
      : '';

    const regionOptions = useMemo(
      () =>
        // "Unassigned" is org-admin only, and not offered while the scope is still unknown.
        buildRegionOptions(scopeQuery.data?.regions || [], jobs, { includeUnassigned: isOrgScope }),
      [scopeQuery.data, jobs, isOrgScope]
    );
    // The picker needs both lists: teams for the rows, jobs for each row's open-requisition count.
    const regionsLoading = scopeQuery.isLoading || jobsLoading;

    // A regional user usually has one region, so start them on their own report instead of an
    // empty page. Once only, so clearing the selection sticks.
    const hasAutoSelectedRef = useRef(false);
    useEffect(() => {
      if (hasAutoSelectedRef.current || !isRegionalScope || regionsLoading) return;
      hasAutoSelectedRef.current = true;
      setSelectedRegionIds(regionOptions.map((option) => option.id));
    }, [isRegionalScope, regionsLoading, regionOptions]);

    // Job posts are derived from the region pick rather than chosen directly, and closed
    // requisitions are dropped: if BK closed the role they do not want it in the report.
    const scopedJobs = useMemo(
      () => scopeJobsToRegions(jobs, selectedRegionIds),
      [jobs, selectedRegionIds]
    );
    const selectedJobIds = useMemo(
      () => scopedJobs.map((job) => String(job._id || job.id)),
      [scopedJobs]
    );
    const selectedJobIdsKey = selectedJobIds.join(',');

    const screeningsQuery = useQuery({
      queryKey: ['reports', 'bk', 'screenings', selectedJobIdsKey],
      // One batched request. Selecting every region scopes ~27 jobs, which previously meant
      // ~27 separate round trips from the browser.
      queryFn: async () => {
        const res = await axios.get('/api/screenings/summary', {
          params: { jobId: selectedJobIdsKey },
        });
        return res.data?.screenings || [];
      },
      enabled: selectedJobIds.length > 0,
      staleTime: 2 * 60 * 1000,
    });

    // The server counts a screening under its own team (falling back to its job's), and refuses a
    // regional user anything outside their teams — so for them, send only what it will accept.
    const screenings = useMemo(() => {
      const all = screeningsQuery.data || [];
      return isRegionalScope ? scopeScreeningsToRegions(all, scopedJobs, selectedRegionIds) : all;
    }, [screeningsQuery.data, isRegionalScope, scopedJobs, selectedRegionIds]);
    const screeningIdsKey = screenings.map((s) => String(s._id || s.id)).join(',');

    useEffect(() => {
      setSelectedScreeningIds(screeningIdsKey ? screeningIdsKey.split(',') : []);
    }, [screeningIdsKey]);

    useEffect(() => {
      if (timeFrame !== 'custom') setRange(getDefaultRange(timeFrame));
    }, [timeFrame]);

    const reportParams = useMemo(() => {
      if (!selectedJobIds.length || !selectedScreeningIds.length || !range.from || !range.to) return null;
      return { jobId: selectedJobIds, screeningId: selectedScreeningIds, from: range.from, to: range.to };
    }, [range.from, range.to, selectedJobIds, selectedScreeningIds]);

    const reportQuery = useQuery({
      queryKey: ['reports', 'screening-report', reportParams],
      queryFn: async () => reportsApi.getScreeningReport(reportParams),
      enabled: !!reportParams,
      retry: false,
    });

    const report = useMemo(() => {
      if (reportMatchesParams(reportQuery.data, reportParams)) return normalizeReportPayload(reportQuery.data, { from: range.from, to: range.to });
      return null;
    }, [range.from, range.to, reportParams, reportQuery.data]);

    const hasLiveData = !!report && reportMatchesParams(reportQuery.data, reportParams);
    const reportErrorMessage =
      reportParams && reportQuery.isError
        ? getApiErrorMessage(reportQuery.error, 'Please try again in a moment.')
        : '';
    const isLoadingReport = reportQuery.isFetching;
    const canLoadLiveReport = !!reportParams;

    const monthColumns = useMemo(() => {
      return getMonthsInRange(range.from, range.to);
    }, [range.from, range.to]);

    /* ── Column axis ──
       Roles come from the scoped job posts, so a role with no applicants still gets a column
       (an empty column is information: nobody applied). Regions come from the picker, in the
       same order it lists them. */
    const roleColumns = useMemo(() => buildRoleColumns(scopedJobs), [scopedJobs]);
    const regionColumns = useMemo(
      () => buildRegionColumns(regionOptions, selectedRegionIds),
      [regionOptions, selectedRegionIds]
    );
    const resolveCandidateRegionId = useMemo(
      () => makeRegionResolver(regionOptions),
      [regionOptions]
    );

    // Built-in panels plus this org's configured application questions.
    const categoryPanels = useMemo(() => getCategoryPanels(report?.meta), [report?.meta]);

    // The export keeps every panel; only the on-screen grid honours `hiddenOnScreen`.
    const visibleCategoryPanels = useMemo(
      () => categoryPanels.filter((panel) => !panel.hiddenOnScreen),
      [categoryPanels]
    );

    const perRoleData = useMemo(() => {
      if (!report?.rawCandidates?.length || !roleColumns.length) return null;
      return computePerColumnBreakdowns(
        report.rawCandidates,
        roleColumns,
        categoryPanels,
        candidateRoleId
      );
    }, [report, roleColumns, categoryPanels]);

    const perRegionData = useMemo(() => {
      if (!report?.rawCandidates?.length || !regionColumns.length) return null;
      return computePerColumnBreakdowns(
        report.rawCandidates,
        regionColumns,
        categoryPanels,
        resolveCandidateRegionId
      );
    }, [report, regionColumns, categoryPanels, resolveCandidateRegionId]);

    const perMonthData = useMemo(() => {
      if (!report?.rawCandidates?.length || !monthColumns?.length) return null;
      return computePerMonthBreakdowns(report.rawCandidates, monthColumns, categoryPanels);
    }, [report, monthColumns, categoryPanels]);

    const activeColumns = useMemo(() => {
      if (comparisonMode === 'month') return monthColumns;
      if (comparisonMode === 'region') return regionColumns;
      return roleColumns;
    }, [comparisonMode, monthColumns, regionColumns, roleColumns]);

    const activeBreakdownData = useMemo(() => {
      if (comparisonMode === 'month') return perMonthData;
      if (comparisonMode === 'region') return perRegionData;
      return perRoleData;
    }, [comparisonMode, perMonthData, perRegionData, perRoleData]);

    // Which candidate lands in which column, for the per-column export sheets.
    const activeColumnResolver = useMemo(() => {
      if (comparisonMode === 'region') return resolveCandidateRegionId;
      if (comparisonMode === 'role') return candidateRoleId;
      return null;
    }, [comparisonMode, resolveCandidateRegionId]);

    // A single column is not a comparison — render it as the plain single-column view.
    const isMultiColumnMode = activeColumns.length > 1;

    /* ── Single-column category data (computed from rawCandidates) ── */
    const singleColumnCategories = useMemo(() => {
      if (!report?.rawCandidates?.length || isMultiColumnMode) return null;
      return computeSingleColumnCategories(report.rawCandidates, categoryPanels);
    }, [report, isMultiColumnMode, categoryPanels]);

    const categoryData = isMultiColumnMode
      ? activeBreakdownData?.categories
      : singleColumnCategories;

    const handleRefreshReport = () => {
      if (!canLoadLiveReport) return;
      reportQuery.refetch();
    };

    return (
      <div className="mx-auto flex w-full max-w-[1680px] flex-col gap-5">
        <style>{customScrollbarCss}</style>
        {/* ── Header row ── */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-950">Reports</h1>
            <p className="mt-1 text-sm text-slate-600">
              {isRegionalScope
                ? `Hiring analytics for your region${regionOptions.length === 1 ? '' : 's'} by hiring position · live, always current.`
                : 'Hiring analytics by region and hiring position · live, always current.'}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative" ref={actionsMenuRef}>
              <button
                type="button"
                onClick={() => setIsActionsOpen((prev) => !prev)}
                aria-haspopup="menu"
                aria-expanded={isActionsOpen}
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none"
              >
                Actions
                <ChevronDown className="h-4 w-4 text-slate-500" />
              </button>
              {isActionsOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg z-50 text-left whitespace-nowrap"
                >
                  <button
                    type="button"
                    onClick={async () => {
                      if (report) {
                        await exportExcel(report, {
                          comparisonMode,
                          monthColumns,
                          perMonthData,
                          columns: activeColumns,
                          perColumnData: activeBreakdownData,
                          getColumnId: activeColumnResolver,
                          columnLabel: COMPARISON_LABELS[comparisonMode] || 'Column',
                          regionNames: regionColumns.map((c) => c.title),
                          // In "By Month" mode the months are the columns, so the stacked
                          // sub-tables nest by hiring position instead.
                          nestColumns: roleColumns,
                          nestGetColumnId: candidateRoleId,
                          nestLabel: COMPARISON_LABELS.role,
                        });
                      }
                      setIsActionsOpen(false);
                    }}
                    disabled={!hasLiveData}
                    className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                  >
                    <Download className="h-4 w-4 text-slate-500" />
                    Download Excel
                  </button>

                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Filter bar ── */}
        <div ref={filterBarRef} className="rounded-2xl border border-slate-200 bg-white px-4 py-4 sm:px-5 shadow-sm">
          {/* Three controls, not four — the MR page has no screening picker, so the grid drops
              the spare 1fr column that was stretching the Generate button across it. */}
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[330px_1fr_auto] xl:items-end">
            <div className="relative">
              <label className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                Time frame
              </label>
              <button
                type="button"
                onClick={() => setIsCustomDateModalOpen((prev) => !prev)}
                className="flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3.5 text-sm font-semibold text-slate-700 shadow-xs hover:border-blue-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/20 transition-all"
              >
                <div className="flex items-center gap-2 truncate">
                  <Calendar className="h-4 w-4 text-[#007BFF] shrink-0" />
                  <span className="truncate text-slate-900 font-bold">
                    {range.from && range.to
                      ? `${formatDateLabel(range.from)} – ${formatDateLabel(range.to)}`
                      : 'Select Date Range'}
                  </span>
                </div>
                <ChevronDown className={cn("h-4 w-4 text-slate-400 shrink-0 transition-transform duration-200", isCustomDateModalOpen && "rotate-180")} />
              </button>

              {/* Attached Floating Interactive Calendar Popover */}
              {isCustomDateModalOpen && (
                <VisualCalendarPopover
                  initialRange={range}
                  onClose={() => setIsCustomDateModalOpen(false)}
                  onApply={(newRange) => {
                    setTimeFrame('custom');
                    setRange(newRange);
                    setIsCustomDateModalOpen(false);
                  }}
                />
              )}
            </div>
            <div className="relative">
              <label className="mb-2 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
                Region{selectedRegionIds.length > 0 ? ` (${selectedRegionIds.length})` : ''}
              </label>
              <button
                type="button"
                onClick={() => setRegionDropdownOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={regionDropdownOpen}
                className="flex h-11 w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-950 outline-none transition focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/20"
              >
                <span className="truncate">
                  {regionsLoading
                    ? 'Loading regions...'
                    : selectedRegionIds.length === 0
                      ? 'Select regions'
                      : selectedRegionIds.length === regionOptions.length
                        ? `All regions (${regionOptions.length})`
                        : selectedRegionIds.length === 1
                          ? regionOptions.find((r) => r.id === selectedRegionIds[0])?.name || 'Selected'
                          : `${selectedRegionIds.length} regions selected`}
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
              </button>
              {regionDropdownOpen && (
                <div
                  role="listbox"
                  className="absolute left-0 top-full z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white py-1 shadow-lg max-h-60 overflow-y-auto minimal-scrollbar"
                >
                  {regionOptions.length === 0 && (
                    <p className="px-4 py-3 text-sm text-slate-500">
                      {regionsLoading
                        ? 'Loading regions...'
                        : scopeErrorMessage || 'No regions configured for this organisation.'}
                    </p>
                  )}
                  {regionOptions.length > 0 && (
                    <label className="flex cursor-pointer items-center justify-between gap-3 px-4 py-2.5 text-sm hover:bg-slate-50 border-b border-slate-100">
                      <div className="flex items-center gap-3 min-w-0">
                        <input
                          type="checkbox"
                          checked={selectedRegionIds.length === regionOptions.length}
                          onChange={() => {
                            setSelectedRegionIds(
                              selectedRegionIds.length === regionOptions.length
                                ? []
                                : regionOptions.map((r) => r.id)
                            );
                          }}
                          className="h-4 w-4 rounded border-slate-300 text-[#007BFF] focus:ring-[#007BFF]/20"
                        />
                        <span className="truncate font-semibold text-slate-900">Select All</span>
                      </div>
                    </label>
                  )}
                  {regionOptions.map((region) => {
                    const checked = selectedRegionIds.includes(region.id);
                    return (
                      <label
                        key={region.id}
                        className="flex cursor-pointer items-center justify-between gap-3 px-4 py-2.5 text-sm hover:bg-slate-50"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setSelectedRegionIds((prev) =>
                                checked ? prev.filter((id) => id !== region.id) : [...prev, region.id]
                              );
                            }}
                            className="h-4 w-4 rounded border-slate-300 text-[#007BFF] focus:ring-[#007BFF]/20"
                          />
                          <span className="truncate">{region.name}</span>
                        </div>
                        {/* Open requisitions only — a region whose roles are all closed reads as empty. */}
                        <span
                          className={cn(
                            'inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold border',
                            region.jobCount > 0
                              ? 'bg-slate-50 text-slate-600 border-slate-200'
                              : 'bg-amber-50 text-amber-800 border-amber-200/80'
                          )}
                        >
                          {region.jobCount > 0
                            ? `${region.jobCount} open job${region.jobCount === 1 ? '' : 's'}`
                            : 'no open jobs'}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>


            <button
              type="button"
              onClick={handleRefreshReport}
              disabled={!canLoadLiveReport}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#007BFF] px-5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none whitespace-nowrap"
            >
              {isLoadingReport ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Generate Report
            </button>
          </div>
          {selectedRegionIds.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-slate-100 pt-4">
              <span className="text-xs text-slate-500">
                {selectedJobIds.length} open job post{selectedJobIds.length === 1 ? '' : 's'} across{' '}
                {selectedRegionIds.length} region{selectedRegionIds.length === 1 ? '' : 's'}
                {screenings.length > 0 ? ` · all ${selectedScreeningIds.length} screenings` : ''}
              </span>
              {selectedJobIds.length === 0 && (
                <span className="text-xs font-semibold text-amber-700">
                  Closed job posts are excluded from this report.
                </span>
              )}
            </div>
          )}
        </div>

        {/* ── Empty / loading state ── */}
        {!report && (
          <EmptyReportState
            isLoading={isLoadingReport}
            errorMessage={reportErrorMessage || scopeErrorMessage}
          />
        )}

        {/* ── Report content ── */}
        {report && (
          <>
            {/* Info strip */}
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0 text-sm text-slate-700">
                <span>Showing </span><span className="rounded-full bg-blue-50 px-2 py-1 text-xs font-black text-[#007BFF]">{report.summary.totalCandidates} candidates</span>
                <span> · {formatDateLabel(report.meta.from)} to {formatDateLabel(report.meta.to)}</span>
              </div>

              {/* Comparison Mode Selector */}
              <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white p-1 shadow-xs shrink-0">
                <span className="px-2 text-[11px] font-bold text-slate-400 uppercase tracking-wider">Compare:</span>
                {['role'].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setComparisonMode(mode)}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-xs font-bold transition-all',
                      comparisonMode === mode
                        ? 'bg-[#007BFF] text-white shadow-xs'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    )}
                  >
                    By {COMPARISON_LABELS[mode]}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
              <OutcomePanel
                report={report}
                isMultiColumnMode={isMultiColumnMode}
                columns={activeColumns}
                breakdownOutcomes={activeBreakdownData?.outcomes}
                activeModal={activeModal}
                setActiveModal={setActiveModal}
              />
              <FunnelPanel
                report={report}
                isMultiColumnMode={isMultiColumnMode}
                columns={activeColumns}
                breakdownFunnel={activeBreakdownData?.funnel}
                activeModal={activeModal}
                setActiveModal={setActiveModal}
              />
            </div>

            {categoryData && (
              <div className="grid grid-cols-6 gap-4">
                {visibleCategoryPanels.map((panel) => (
                  <div key={panel.key} className="col-span-6 min-w-0 lg:col-span-3">
                    <CategoryPanel
                      title={panel.chartTitle}
                      data={capCategoryData(categoryData[panel.key], panel.topN, activeColumns.map((c) => c.id))}
                      isMultiColumnMode={isMultiColumnMode}
                      columns={activeColumns}
                      panelId={panel.key}
                      activeModal={activeModal}
                      setActiveModal={setActiveModal}
                    />
                  </div>
                ))}
              </div>
            )}

            <TimelinePanel
              report={report}
              isMultiColumnMode={isMultiColumnMode}
              columns={activeColumns}
              perColumnData={activeBreakdownData}
              comparisonMode={comparisonMode}
              monthColumns={monthColumns}
              columnLabel={COMPARISON_LABELS[comparisonMode]}
            />
            <RosterPanel report={report} columns={activeColumns} comparisonMode={comparisonMode} />
            {activeModal && (
              <VisualModal
                activeModal={activeModal}
                onClose={() => setActiveModal(null)}
                report={report}
                isMultiColumnMode={isMultiColumnMode}
                columns={activeColumns}
                breakdownData={activeBreakdownData}
                categoryData={categoryData}
              />
            )}
          </>
        )}

              </div>
  );
}